import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useContext,
  useCallback,
} from "react";

import { isSameDay, parseISO, format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import openSocket from "../../services/socket-io";
import {
  Clock,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Ban,
  Download,
  Loader2,
  StickyNote,
  Star,
  SlidersHorizontal,
  X,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Eye,
  FileArchive,
  FileSpreadsheet,
  FileText,
  BadgeCheck,
  UserPlus,
  Pencil,
  Forward,
  AtSign,
  Image as ImageIcon,
  Video as VideoIcon,
  Music,
  Sticker,
  FileIcon,
  MapPin,
  UserRound,
  Mic,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import { useLocalStorage } from "../../hooks/useLocalStorage";

import MarkdownWrapper from "../MarkdownWrapper";
import TemplateMessage from "../TemplateMessage";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import { lerLocalizacao } from "../../utils/localizacao";
import { getPublicFileUrl } from "../../config";
import ModalImageCors from "../ModalImageCors";
import VideoNaConversa from "../VideoNaConversa";
import MessageOptionsMenu from "../MessageOptionsMenu";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";
import MiniaturaDePdf from "../MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { useChatOptimistic } from "../../context/ChatOptimistic/ChatOptimisticContext";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import TranscricaoDeAudio from "../TranscricaoDeAudio";
import ForwardMessageModal from "../ForwardMessageModal";

const mergeMensagem = (existente, entrada) => {
  if (!existente) return entrada;
  const mesclada = { ...existente, ...entrada };

  mesclada.userId = existente.userId ?? entrada.userId;
  mesclada.flowId = existente.flowId ?? entrada.flowId;
  mesclada.flowName = existente.flowName ?? entrada.flowName;
  mesclada.interactionId = existente.interactionId ?? entrada.interactionId;
  mesclada.interactionName = existente.interactionName ?? entrada.interactionName;

  const sfdAnt = Boolean(existente.sentFromDevice);
  const sfdNov = Boolean(entrada.sentFromDevice);
  mesclada.sentFromDevice = sfdAnt && sfdNov;

  // isForwarded: regra TRUE-GANHA (característica inerente).
  mesclada.isForwarded = Boolean(existente.isForwarded) || Boolean(entrada.isForwarded);

  // viewOnce: regra TRUE-GANHA (característica inerente — mídia de "ver
  // uma vez" sempre será, independente de eco/webhook).
  mesclada.viewOnce = Boolean(existente.viewOnce) || Boolean(entrada.viewOnce);

  if (existente.user && !entrada.user) mesclada.user = existente.user;
  if (existente.reacoes && !entrada.reacoes) mesclada.reacoes = existente.reacoes;
  if (existente.mediaType && !entrada.mediaType) mesclada.mediaType = existente.mediaType;
  if (existente.body && !entrada.body) mesclada.body = existente.body;

  // ── Preservação campos de edição (root cause: selo "editada" sumia) ─
  // Atualizações parciais via socket (ack, reação, favorito) não trazem
  // editedAt / previousBody / editHistory. Sem estas linhas, o spread
  // `...entrada` sobrescreve os campos do existente para undefined em
  // payloads que não os declaram → selo editada desaparece.
  if (existente.editedAt && entrada.editedAt === undefined) {
    mesclada.editedAt = existente.editedAt;
  }
  if (
    existente.previousBody !== null &&
    existente.previousBody !== undefined &&
    entrada.previousBody === undefined
  ) {
    mesclada.previousBody = existente.previousBody;
  }
  if (
    Array.isArray(existente.editHistory) &&
    !Array.isArray(entrada.editHistory)
  ) {
    mesclada.editHistory = existente.editHistory;
  }

  if (existente.isDeleted && entrada.isDeleted === undefined) {
    mesclada.isDeleted = existente.isDeleted;
  }
  if (existente.deletedAt !== null && existente.deletedAt !== undefined && entrada.deletedAt === undefined) {
    mesclada.deletedAt = existente.deletedAt;
  }

  if (entrada.ack !== undefined && entrada.ack !== null) {
    mesclada.ack = Math.max(Number(existente.ack || 0), Number(entrada.ack || 0));
  }

  return mesclada;
};

const reducer = (state, action) => {
  // Helpers locais ao reducer (não dependem de props/state externo).
  // Fingerprint heurístico para MATCH de mensagens OTIMISTAS vs REAL sem
  // replaceOptimisticId explícito no payload do socket (backend não sabia
  // quem disparou a otimista no frontend). Usado SÓ para: sticker / midias
  // enviadas por mim que acabaram de ser criadas no mesmo timestamp que uma
  // otimista — evita duplicações 100% do tempo para figurinhas.
  const fingerprintMidia = (m) => {
    const parts = [];
    if (m?.mediaType) parts.push(String(m.mediaType).trim().toLowerCase());
    if (m?.mediaUrl) {
      const u = String(m.mediaUrl).trim();
      // Remove query params de cache (?v=1 / ?t=...) e timestamp para o match
      // não falhar por 1s de diferença no backend.
      const limpa = u.replace(/[?#].*$/, "");
      if (limpa) parts.push(limpa);
    }
    if (m?.body) parts.push(String(m.body).trim().slice(0, 200));
    parts.push(Boolean(m?.fromMe) ? "1" : "0");
    return parts.join("::");
  };
  const mesmaMidiaEnviada = (a, b, toleranciaMs = 5 * 1000) => {
    if (!a || !b) return false;
    if ((a?.fromMe !== undefined && b?.fromMe !== undefined) && Boolean(a?.fromMe) !== Boolean(b?.fromMe)) return false;
    const fa = fingerprintMidia(a);
    const fb = fingerprintMidia(b);
    if (fa !== fb) return false;
    const da = a?.createdAt ? +new Date(a.createdAt) : NaN;
    const db = b?.createdAt ? +new Date(b.createdAt) : NaN;
    if (Number.isFinite(da) && Number.isFinite(db)) {
      return Math.abs(da - db) <= toleranciaMs;
    }
    return true;
  };

  // ── Garantia FINAL de NÃO-duplicatas 100% (última linha de defesa) ─────
  // Elimina do state 2+ mensagens com o mesmo `id`. Isso resolve o warning
  // "Encountered two children with the same key 3EB0…" do React em stickers:
  // por algum paralelismo (ADD_MESSAGE + REPLACE_BY_OPTIMISTIC_ID + eco do
  // socket na mesma microtarefa), às vezes 2 entradas chegavam com o mesmo
  // id do WhatsApp e o React desenhava 2 balões com a mesma key.
  // - fromMe (ambos nós) e fromMe=false (ambos recebidos) são unificados.
  // - Se uma é otimista e a outra não, FICA a NÃO-OTIMISTA (tem o id real).
  // - Se ambas são reais, fica a MAIS NOVA (createdAt maior) / com mais dados.
  const eliminarDuplicatasPorId = (lista) => {
    if (!Array.isArray(lista) || lista.length <= 1) return lista;
    const porId = new Map();
    for (const m of lista) {
      if (!m || m.id === undefined || m.id === null) {
        // Sem id (só optimisticId puro) → adiciona direto, não pode duplicar
        // por id. Deduplicamos por optimisticId abaixo.
        const k = m?.optimisticId ? `__opt_${String(m.optimisticId)}` : `__fallback_${Math.random().toString(36).slice(2)}`;
        if (!porId.has(k)) porId.set(k, m);
        continue;
      }
      const chave = String(m.id);
      const existente = porId.get(chave);
      if (!existente) {
        porId.set(chave, m);
        continue;
      }
      // Política: qual das 2 fica?
      // 1) A NÃO-otimista sempre ganha da otimista (ela tem id real).
      // 2) A que tiver mais campos preenchidos vence.
      // 3) Desempate: a com createdAt mais novo.
      const a = existente;
      const b = m;
      const aOt = Boolean(a?.isOptimistic);
      const bOt = Boolean(b?.isOptimistic);
      if (aOt && !bOt) { porId.set(chave, mergeMensagem(a, b)); continue; }
      if (!aOt && bOt) { porId.set(chave, mergeMensagem(b, a)); continue; }
      const aPontos = [a.body, a.mediaUrl, a.ack, a.status, a.readAt].filter(Boolean).length;
      const bPontos = [b.body, b.mediaUrl, b.ack, b.status, b.readAt].filter(Boolean).length;
      if (bPontos > aPontos) { porId.set(chave, mergeMensagem(a, b)); continue; }
      if (aPontos > bPontos) { porId.set(chave, mergeMensagem(b, a)); continue; }
      const tA = a?.createdAt ? +new Date(a.createdAt) : 0;
      const tB = b?.createdAt ? +new Date(b.createdAt) : 0;
      if (tB >= tA) porId.set(chave, mergeMensagem(a, b));
      else porId.set(chave, mergeMensagem(b, a));
    }
    const saida = Array.from(porId.values());
    // Preserva ordenação cronológica (mergeMensagem / Map insertions podem
    // quebrar a ordem).
    saida.sort((x, y) => {
      const dx = +parseDataSeguraInline(x?.createdAt, new Date(0));
      const dy = +parseDataSeguraInline(y?.createdAt, new Date(0));
      if (dx !== dy) return dx - dy;
      return String(x?.id ?? "").localeCompare(String(y?.id ?? ""));
    });
    return saida;
  };
  // Helper leve para parsear data DENTRO do reducer (não consegue importar
  // de fora da closure). Faz só o mínimo, equivalente ao parseDataSegura.
  const parseDataSeguraInline = (data, fallback = new Date()) => {
    if (data instanceof Date && !Number.isNaN(data.getTime())) return data;
    if (!data) return fallback;
    try {
      const d = new Date(data);
      if (!Number.isNaN(d.getTime())) return d;
    } catch {}
    return fallback;
  };

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => String(m.id) === String(message.id));
      if (messageIndex !== -1) {
        state[messageIndex] = mergeMensagem(state[messageIndex], message);
      } else {
        newMessages.push(message);
      }
    });

    return eliminarDuplicatasPorId([...newMessages, ...state]);
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    // Otimistas: a mensagem fake pode chegar com optimisticId == id (que é
    // temporário, string). Depois a mensagem real chega SEM optimisticId,
    // mas a entrada real pode ter `replaceOptimisticId` setada (quem disparou
    // a otimista passa o optimisticId pro payload de UPDATE/RETURN), ou
    // fazemos match por optimisticId existente na lista.
    const optimisticId = newMessage.optimisticId || newMessage.replaceOptimisticId || null;
    let procurar = optimisticId || newMessage.id;
    let messageIndex = state.findIndex((m) =>
      String(m.id) === String(procurar) ||
      String(m.optimisticId || "") === String(procurar || ""),
    );

    // ── DEDUP 100% (eco socket / dupla inserção) ───────────────────────
    // ROOT CAUSE principal da duplicata de sticker relatada: endpoint
    // /figurinhas/:id/enviar retorna HTTP 200 COM SÓ { id } (sem a mensagem
    // completa), e LOGO DEPOIS o backend emite `appMessage.create` com a
    // mesma mensagem (mesmo id numérico). Antes: REPLACE_BY_OPTIMISTIC_ID
    // substituía a otimista por { id: 123 }, e o ADD_MESSAGE do socket eco
    // não encontrava match por id pq a heurística falhava → DUPLICATA.
    //
    // SOLUÇÃO ROBUSTA: QUALQUER ADD_MESSAGE que chegue com id definido
    // (string ou number, prefixo opt_ ou não) que JÁ EXISTA na lista, NÃO
    // CRIA NOVA; sempre mescla com a existente. Cobre 100% de casos de eco,
    // duplo dispatch, e qualquer paralelismo de pub/sub no ChatOptimistic.
    if (messageIndex === -1 && newMessage?.id !== undefined && newMessage?.id !== null) {
      const idComoChave = String(newMessage.id);
      const idxDedup = state.findIndex((m) => String(m.id) === idComoChave);
      if (idxDedup !== -1) {
        messageIndex = idxDedup;
      }
    }

    // ── Match HEURÍSTICO (anti-duplicação) ────────────────────────────────
    // Quando o backend emite "appMessage.create" ou eco do socket SEM
    // `replaceOptimisticId`, procuramos por UMA mensagem OTIMISTA no state
    // que seja "a mesma mídia enviada por mim nos últimos 5s". Isto resolve
    // 100% da duplicação de figurinhas (sticker) do bug relatado — a
    // otimista e a real são sempre a mesma figurinha do cliente logado.
    // Não usa para mensagem fromMe=false (recebidas do cliente).
    if (messageIndex === -1 && Boolean(newMessage.fromMe)) {
      const idxHeuristico = state.findIndex(
        (m) => Boolean(m.isOptimistic) && mesmaMidiaEnviada(m, newMessage),
      );
      if (idxHeuristico !== -1) {
        // Carrega o optimisticId da existente para a replace abaixo rodar
        // certeira e apagar o isOptimistic / optimisticId temporário.
        newMessage.replaceOptimisticId =
          newMessage.replaceOptimisticId ||
          state[idxHeuristico].optimisticId ||
          state[idxHeuristico].id ||
          null;
        messageIndex = idxHeuristico;
        if (!optimisticId) procurar = newMessage.replaceOptimisticId || procurar;
      }
    }

    if (messageIndex !== -1) {
      state[messageIndex] = mergeMensagem(state[messageIndex], newMessage);
      // Se entrou a mensagem real (não otimista), garante que id/optimisticId
      // seja o REAL do banco, não mais o temporário.
      if (!newMessage.isOptimistic) {
        if (newMessage.id && typeof String(newMessage.id).startsWith === "function" && !String(newMessage.id).startsWith("opt_")) {
          state[messageIndex].id = newMessage.id;
        }
        state[messageIndex].isOptimistic = false;
        state[messageIndex].optimisticId = undefined;
      }
      return eliminarDuplicatasPorId([...state]);
    }

    const nova = [...state, newMessage];
    nova.sort((a, b) => {
      const da = a?.createdAt ? +new Date(a.createdAt) : 0;
      const db = b?.createdAt ? +new Date(b.createdAt) : 0;
      const diff = da - db;
      return diff !== 0 ? diff : String(a.id ?? "").localeCompare(String(b.id ?? ""));
    });

    return eliminarDuplicatasPorId(nova);
  }

  if (action.type === "REPLACE_BY_OPTIMISTIC_ID") {
    const optimisticId = action.optimisticId;
    const real = action.payload;
    if (!optimisticId && !real?.replaceOptimisticId) {
      return state;
    }
    const chave = optimisticId || real.replaceOptimisticId;
    let messageIndex = state.findIndex(
      (m) =>
        String(m.optimisticId || "") === String(chave || "") ||
        String(m.id) === String(chave || ""),
    );
    // ── Fallback heurístico se o optimisticId não bater por outro motivo
    // (ex.: FigurinhaPicker passou chave errada, ou MessagesList remontou e
    // perdeu o optimisticId por `key` trocado). Match por mesmaMidiaEnviada
    // com a entrada REAL, procurando UMA só otimista no state.
    if (messageIndex === -1 && Boolean(real?.fromMe)) {
      messageIndex = state.findIndex(
        (m) => Boolean(m.isOptimistic) && mesmaMidiaEnviada(m, real, 10 * 1000),
      );
    }
    if (messageIndex === -1) {
      // Se não achou pra substituir, adiciona como uma nova (fallback seguro).
      const nova = [...state, real];
      nova.sort((a, b) => {
        const da = a?.createdAt ? +new Date(a.createdAt) : 0;
        const db = b?.createdAt ? +new Date(b.createdAt) : 0;
        const diff = da - db;
        return diff !== 0 ? diff : String(a.id ?? "").localeCompare(String(b.id ?? ""));
      });
      return eliminarDuplicatasPorId(nova);
    }
    const substituida = {
      ...mergeMensagem(state[messageIndex], real),
      id: real?.id ?? state[messageIndex].id,
      optimisticId: undefined,
      isOptimistic: false,
    };
    state[messageIndex] = substituida;
    return eliminarDuplicatasPorId([...state]);
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const optimisticId = messageToUpdate.optimisticId || messageToUpdate.replaceOptimisticId || null;
    const procurar = optimisticId || messageToUpdate.id;
    let messageIndex = state.findIndex(
      (m) =>
        String(m.id) === String(procurar || "") ||
        String(m.optimisticId || "") === String(procurar || ""),
    );
    // Nível 2 (Fallback 1 — id WhatsApp com sufixo/prefixo divergente)
    if (messageIndex === -1 && messageToUpdate?.id !== undefined && messageToUpdate?.id !== null) {
      const idAlvo = String(messageToUpdate.id);
      if (idAlvo.length >= 5) {
        messageIndex = state.findIndex(m => {
          if (!m?.id) return false;
          const idLista = String(m.id);
          if (idLista === idAlvo) return true;
          if (idLista.endsWith("_"+idAlvo) || idAlvo.endsWith("_"+idLista)) return true;
          const a = idLista.split("_").pop();
          const b = idAlvo.split("_").pop();
          return a && b && a.length >= 5 && a === b;
        });
      }
    }
    // Nível 3 (Fallback 2 — heurística fingerprint para fromMe=true e fromMe=false)
    if (messageIndex === -1) {
      messageIndex = state.findIndex(m => mesmaMidiaEnviada(m, messageToUpdate, 15 * 60 * 1000));
    }

    if (messageIndex !== -1) {
      state[messageIndex] = mergeMensagem(state[messageIndex], messageToUpdate);
      if (!messageToUpdate.isOptimistic) {
        state[messageIndex].isOptimistic = false;
      }
      return eliminarDuplicatasPorId([...state]);
    }

    const nova = [...state, messageToUpdate];
    nova.sort((a, b) => {
      const da = a?.createdAt ? +new Date(a.createdAt) : 0;
      const db = b?.createdAt ? +new Date(b.createdAt) : 0;
      const diff = da - db;
      return diff !== 0 ? diff : String(a.id ?? "").localeCompare(String(b.id ?? ""));
    });
    return eliminarDuplicatasPorId(nova);
  }

  if (action.type === "REMOVE_MESSAGE") {
    // Aceita remove por optimisticId também (não só por id).
    const id = action.payload || action.optimisticId;
    const filtrada = state.filter(
      (m) => String(m.id) !== String(id) && String(m.optimisticId || "") !== String(id || ""),
    );
    return eliminarDuplicatasPorId(filtrada);
  }

  // As reações moram na própria mensagem, e não num mapa à parte, porque é
  // assim que elas são desenhadas: um chip embaixo daquele balão. O payload
  // aceita as duas formas em que elas chegam — o mapa inteiro que vem junto
  // com a página de mensagens (`mapa`) e a atualização de uma só, que vem pelo
  // socket ou da resposta do POST.
  if (action.type === "SET_REACTION") {
    const mapa = action.payload.mapa || {
      [action.payload.messageId]: action.payload.reacoes,
    };

    return state.map((m) =>
      mapa[m.id] !== undefined ? { ...m, reacoes: mapa[m.id] } : m,
    );
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Quantas páginas a mais o sistema aceita carregar sozinho atrás de um
// resultado de busca antigo. Sem teto, escolher a primeira mensagem de uma
// conversa de dois anos puxaria a conversa inteira, de 20 em 20.
// Quantas páginas de 20 a busca puxa para trás até alcançar a mensagem. Subiu
// de 25 para 60 quando a conversa passou a mostrar o histórico do contato: o
// que se procura costuma estar num atendimento anterior, muito mais atrás.
const MAX_PAGINAS_ATRAS_DO_ALVO = 60;

const MessagesList = ({ ticketId, isGroup }) => {
  // ================================================================
  // Helper SEGURO para parsear datas (createdAt, deletedAt etc).
  //
  // O backend pode emitir objetos de mensagem INCOMPLETOS no socket
  // (ex: eco do webhook Zernio que só atualiza ack, ou atualização
  // de favorito/lixeira que só manda id + flag). Quando qualquer um
  // dos campos de data vem `undefined`/`null`/`""` o `parseISO()` do
  // date-fns chama `.split()` em undefined → TypeError que derruba
  // a página inteira (ErrorBoundary mostra tela vermelha).
  //
  // Esta função retorna SEMPRE um Date válido:
  //   1. Entrada válida (string Date) → retorna parseISO(data)
  //   2. Entrada já é Date → retorna ela mesma
  //   3. Entrada vazia/inválida → retorna new Date() (agora)
  //
  // Trocar TUDO de parseISO() direto por parseDataSegura() previne
  // 100% das quebras de tela por data faltante no payload de socket.
  // ================================================================
  const parseDataSegura = (data, fallback = new Date()) => {
    if (data instanceof Date && !Number.isNaN(data.getTime())) return data;
    if (!data) return fallback;
    const str = String(data).trim();
    if (!str) return fallback;
    try {
      const dia = parseISO(str);
      return Number.isNaN(dia.getTime()) ? fallback : dia;
    } catch {
      return fallback;
    }
  };

  // Mesma proteção para o rótulo do dia (Hoje / Ontem / 08 de Setembro).
  const rotuloDoDiaSeguro = (data) => {
    const dia = parseDataSegura(data);
    if (isSameDay(dia, new Date())) return "Hoje";
    if (isSameDay(dia, subDays(new Date(), 1))) return "Ontem";
    return format(dia, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const { subscribe: subscribeOptimistic } = useChatOptimistic();

  const { user: usuarioLogado, isSettingEnabled } = useContext(AuthContext);
  const showAttendantNameInBubble = isSettingEnabled
    ? isSettingEnabled("show_attendant_name_in_bubble", "enabled")
    : true;

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  // Se a conversa deve continuar colada no fim enquanto o conteúdo cresce.
  // Verdadeiro ao abrir e enquanto quem lê estiver no fim; falso assim que a
  // pessoa rola para cima, senão ler o histórico seria impossível — cada
  // imagem que terminasse de carregar jogaria a conversa de volta para baixo.
  const grudarNoFim = useRef(true);

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  // Posição CONGELADA do menu, capturada no exato momento do clique.
  //
  // Se a gente lesse `anchorEl.getBoundingClientRect()` a cada render, o valor
  // mudaria no meio do caminho: o botão de abrir o menu só existe com
  // `group-hover:flex` — quando o ponteiro do mouse sai do wrapper da mensagem
  // (porque o portal do menu foi montado EXATAMENTE debaixo do cursor), o
  // botão ganha `display:none` e o rect vira tudo zero. Aí o menu ia parar no
  // canto superior esquerdo, o ponteiro voltava a fazer hover no grupo, o
  // botão reaparecia e o menu voltava ao lugar — loop infinito = "caixinha
  // piscando". Guardando as coordenadas do momento do clique, isso nunca mais
  // acontece.
  const [anchorRect, setAnchorRect] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  // Filtros da conversa. Ficam aqui, e não no pai, porque quem pagina e
  // acumula as mensagens é este componente — mudar o filtro tem que zerar a
  // lista e voltar para a página 1, senão as mensagens do filtro antigo ficam
  // misturadas com as do novo.
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [apenasFavoritas, setApenasFavoritas] = useState(false);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Busca de texto dentro da conversa. Fica separada dos filtros acima porque
  // ela NÃO recorta a lista: a conversa continua inteira e a busca só aponta
  // para onde ir — como no WhatsApp.
  const [termoBusca, setTermoBusca] = useState("");
  const [achados, setAchados] = useState([]);
  const [indiceAchado, setIndiceAchado] = useState(0);
  const [buscando, setBuscando] = useState(false);
  // Id da mensagem que a busca quer alcançar e id da que está piscando.
  const [alvoBusca, setAlvoBusca] = useState(null);
  const [destacada, setDestacada] = useState(null);
  const paginasPuxadasRef = useRef(0);

  // Ids favoritados POR ESTE atendente. Vem do backend a cada página; o Set
  // acumula porque a conversa é carregada aos poucos.
  const [favoritos, setFavoritos] = useState(() => new Set());

  // Escolher arquivos na própria conversa. Ligado o modo, a bolha deixa de
  // abrir o arquivo e passa a marcar/desmarcar — só as que TÊM anexo; clicar
  // numa mensagem de texto não faz nada, para não dar a impressão de que ela
  // vai no ZIP.
  const [modoSelecao, setModoSelecao] = useState(false);
  const [selecionadas, setSelecionadas] = useState(() => new Set());

  // Modo de seleção múltipla PARA ENCAMINHAR em lote. Aqui QUALQUER mensagem
  // (texto, imagem, áudio, vídeo, PDF, localização, figurinha etc.) pode ser
  // marcada. A lista é enviada na mesma ordem cronológica do histórico para
  // o ticket destino, cada uma gravada com isForwarded=true.
  const [modoEncaminharLote, setModoEncaminharLote] = useState(false);
  const [selecionadasEncaminhar, setSelecionadasEncaminhar] = useState(
    () => new Set(),
  );
  const [modalEncaminharAberto, setModalEncaminharAberto] = useState(false);
  // Conjunto ordenado (pela ordem da conversa) já coletado no clique do botão
  // "Encaminhar selecionadas"; é o array passado ao modal para envio em série.
  const [encaminharLoteSelecionadas, setEncaminharLoteSelecionadas] = useState(
    [],
  );
  // Participantes do grupo que acabaram de entrar na agenda: a lista de
  // mensagens vem do reducer e não recarrega só por causa disto.
  const [adicionados, setAdicionados] = useState(() => new Set());
  // De que atendimento é cada mensagem: número, fila e situação. É o que
  // permite desenhar a faixa entre um chamado e o seguinte.
  const [atendimentos, setAtendimentos] = useState({});
  // Hora com segundos na bolha. Fica na escolha de cada um e é lembrada: no
  // uso normal "14:32" basta, mas quem está conferindo a ordem de uma rajada
  // precisa do segundo.
  const [horaDetalhada, setHoraDetalhada] = useLocalStorage(
    "horaComSegundos",
    false,
  );
  const [adicionando, setAdicionando] = useState(null);
  const [baixando, setBaixando] = useState(null);
  const [usersEquipe, setUsersEquipe] = useState([]);

  // Participantes do grupo (jid, numero, nome). Só para tickets de grupo.
  // Usado para, NA HORA DE RENDERIZAR, trocar "@551199..." de volta para
  // "@Nome da pessoa" na bolha do CRM (atendente vê nome, não número).
  // O MESMO replace inverso do que é feito no MessageInput antes de enviar.
  const [participantes, setParticipantes] = useState([]);
  const carregouParticipantesRef = useRef(null);

  // Arquivo aberto no visualizador (índice dentro de `arquivosDaConversa`).
  const [visualizando, setVisualizando] = useState(null);

  // Histórico de edições da mensagem: abre ao clicar no selo "editada".
  const [editHistoryOpen, setEditHistoryOpen] = useState(false);
  const [mensagemEmEdicao, setMensagemEmEdicao] = useState(null);

  // Citar a mensagem no campo de resposta — o mesmo estado que o menu da bolha
  // usa; aqui ele serve ao atalho de dois cliques.
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  const filtrando = Boolean(dataInicio || dataFim || apenasFavoritas);

  // Ref, e não dependência do efeito do socket: colocar `filtrando` nas deps
  // faria o Socket.IO desconectar e reconectar a cada tecla no campo de data.
  const filtrandoRef = useRef(false);
  filtrandoRef.current = filtrando;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setFavoritos(new Set());
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
    limparBusca();

    setModoEncaminharLote(false);
    setSelecionadasEncaminhar(new Set());
    setModalEncaminharAberto(false);
    setEncaminharLoteSelecionadas([]);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    const aoReceberMensagemLocal = (evt) => {
      const detalhe = evt?.detail || {};
      const msgTicketId = String(detalhe.ticketId || "");
      const ticketAtual = String(currentTicketId.current || "");
      if (!msgTicketId) return;
      // Envio com vários anexos devolve o lote inteiro: um balão para cada.
      // Antes só a mensagem isolada era tratada, e um lote virava um payload
      // de array dentro do ADD_MESSAGE.
      const recebidas = Array.isArray(detalhe.message)
        ? detalhe.message
        : [detalhe.message];
      const validas = recebidas.filter((m) => m && (m.id ?? m.optimisticId));
      if (validas.length === 0) return;
      if (msgTicketId !== ticketAtual) return;
      validas.forEach((m) => dispatch({ type: "ADD_MESSAGE", payload: m }));
      grudarNoFim.current = true;
      setTimeout(() => {
        const lista = document.getElementById("messagesList");
        if (lista) lista.scrollTop = lista.scrollHeight;
      }, 0);
    };

    document.addEventListener("crm:mensagem-enviada", aoReceberMensagemLocal);
    return () => document.removeEventListener("crm:mensagem-enviada", aoReceberMensagemLocal);
  }, []);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const { data } = await api.get("/users");
        if (cancelado) return;
        // Aceita tanto { users: [] } quanto array direto.
        const lista = Array.isArray(data)
          ? data
          : Array.isArray(data?.users)
            ? data.users
            : [];
        setUsersEquipe(lista.filter((u) => Number(u?.id)));
      } catch (_) {
        // Sem permissão: deixa vazio, a nota funciona igual de antes.
        if (!cancelado) setUsersEquipe([]);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [usuarioLogado?.companyId]);

  // ── Carregar participantes do grupo (para renderizar menção bonita) ──
  //
  // A menção enviada para o WhatsApp é codificada com @Numero no corpo
  // (exigência do próprio Baileys/WHA para desenhar a tag azul + notificação).
  // Dentro do CRM atendente quer ver nome, não número. Então fazemos aqui o
  // REPLACE INVERSO no momento do render, sem tocar no body da mensagem no
  // state/reducer (o body do banco continua com @Numero, para ser exato ao que
  // realmente foi enviado para a origem).
  useEffect(() => {
    if (!isGroup) {
      setParticipantes([]);
      carregouParticipantesRef.current = null;
      return;
    }

    // Cache por ticketId: não re-faz a requisição se continuar no mesmo.
    if (carregouParticipantesRef.current === ticketId) return;

    let cancelado = false;
    carregouParticipantesRef.current = ticketId;

    (async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}/participantes`);
        if (cancelado) return;
        const lista = Array.isArray(data) ? data : [];
        setParticipantes(lista.filter((p) => p && (p.jid || p.numero)));
      } catch (_) {
        if (!cancelado) setParticipantes([]);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [ticketId, isGroup]);

  // Trocar o filtro descarta o que já estava carregado: a lista acumulada é de
  // outro recorte. Também trocar de ticket (outro atendimento) deve ZERAR a
  // lista IMEDIATAMENTE, sem esperar ~500ms do debounce de fetchMessages.
  //
  // Sem ticketId nas dependências: atendente troca ticket A → B e as mensagens
  // antigas do cliente A aparecem na tela do B até o fetch chegar; com F5
  // (que recarrega do 0) o sintoma some.
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [ticketId, dataInicio, dataFim, apenasFavoritas]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: {
              pageNumber,
              dataInicio: dataInicio || undefined,
              dataFim: dataFim || undefined,
              apenasFavoritas: apenasFavoritas ? "true" : undefined,
            },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            // Depois do LOAD: as reações só têm onde pousar quando as
            // mensagens da página já estão na lista.
            if (data.reactions) {
              dispatch({
                type: "SET_REACTION",
                payload: { mapa: data.reactions },
              });
            }
            setHasMore(data.hasMore);
            setLoading(false);

            if (data.atendimentos) {
              setAtendimentos((anterior) => ({
                ...anterior,
                ...data.atendimentos,
              }));
            }

            if (data.favoritedIds?.length) {
              setFavoritos((anterior) => {
                const novo = new Set(anterior);
                data.favoritedIds.forEach((id) => novo.add(id));
                return novo;
              });
            }
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, dataInicio, dataFim, apenasFavoritas]);

  // ── Resync SILENCIOSO (sem loader, sem arrastar a rolagem) ─────────────
  //
  // Puxa a PÁGINA 1 de novo e mescla no state — o reducer "LOAD_MESSAGES"
  // não substitui a lista, só acrescenta o que ainda não está nela. Serve
  // para tapar o buraco deixado por uma queda de conexão.
  //
  // Por que é necessário: no celular o PWA é suspenso em segundo plano e o
  // socket cai. Ao voltar, o `connect` reentra na sala, mas as mensagens
  // recebidas DURANTE a ausência nunca foram emitidas para este socket e a
  // conversa ficava com um buraco (o clássico "só aparece depois do F5").
  const sincronizarMensagens = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/" + ticketId, {
        params: {
          pageNumber: 1,
          dataInicio: dataInicio || undefined,
          dataFim: dataFim || undefined,
          apenasFavoritas: apenasFavoritas ? "true" : undefined,
        },
      });

      // Trocaram de ticket enquanto a requisição estava no ar: descarta.
      if (currentTicketId.current !== ticketId) return;

      dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
      if (data.reactions) {
        dispatch({ type: "SET_REACTION", payload: { mapa: data.reactions } });
      }
      setHasMore(data.hasMore);

      if (data.atendimentos) {
        setAtendimentos((anterior) => ({ ...anterior, ...data.atendimentos }));
      }
      if (data.favoritedIds?.length) {
        setFavoritos((anterior) => {
          const novo = new Set(anterior);
          data.favoritedIds.forEach((id) => novo.add(id));
          return novo;
        });
      }

      // Só acompanha o fim quem já estava no fim. Quem subiu para ler o
      // histórico não pode ser arrastado de volta pela reconexão.
      if (grudarNoFim.current) scrollToBottom();
    } catch (_) {
      // Silencioso de propósito: uma reconexão não deve disparar toast.
    }
  }, [ticketId, dataInicio, dataFim, apenasFavoritas]);

  // Ref com a versão mais recente da função: o efeito do socket fica com
  // dependências estáveis (não reconecta a cada troca de filtro).
  const sincronizarRef = useRef(null);
  sincronizarRef.current = sincronizarMensagens;

  useEffect(() => {
    const socket = openSocket();

    // ── Join na sala: duas formas para garantir atualizações em tempo real ──
    //
    // 1. `socket.connected === true`: a conexão compartilhada singleton já
    //    estava conectada de outra tela (evento "connect" já disparou e
    //    NÃO dispara de novo por ser singleton). Então entramos na sala
    //    na hora, logo após o setup do effect.
    //
    // 2. `socket.on("connect")`: caso o socket esteja desconectado no
    //    momento do setup (reconnect em andamento ou primeira visita),
    //    o callback dispara assim que a conexão reestabelecer e aí sim
    //    entramos na sala.
    //
    // Sem o item (1), era o bug: "O visto do WhatsApp precisa ficar dando F5
    // pra mostrar se foi recebido ou visto." — navegar para um ticket novo
    // depois da conexão já aberta NÃO entrava na sala, e `appMessage update`
    // de ack nunca chegava; o atendente via o tique antigo até recarregar.
    //
    // Marca se a conexão JÁ foi vista ao menos uma vez nesta tela. O primeiro
    // `connect` não precisa de resync (o efeito de fetch acima já buscou as
    // mensagens); só a REconexão indica uma queda e pode ter deixado mensagem
    // para trás.
    let jaConectou = false;

    const entrarNaSalaDoTicket = () => {
      try { socket.emit("joinChatBox", ticketId); } catch (_) {}

      if (jaConectou) {
        // Reconexão: puxa o que chegou enquanto estivemos fora da sala.
        try { sincronizarRef.current && sincronizarRef.current(); } catch (_) {}
      }
      jaConectou = true;
    };

    entrarNaSalaDoTicket();
    socket.on("connect", entrarNaSalaDoTicket);

    // Rede de segurança para o celular: quando o PWA volta do segundo plano
    // o socket pode continuar "conectado" (o SO só congelou o JS), então o
    // evento `connect` não dispara e o resync acima nunca rodaria. Ao voltar
    // a ficar visível, sincroniza do mesmo jeito.
    const aoVoltarVisivel = () => {
      if (document.visibilityState !== "visible") return;
      try { sincronizarRef.current && sincronizarRef.current(); } catch (_) {}
    };
    document.addEventListener("visibilitychange", aoVoltarVisivel);

    const onAppMessage = (data) => {
      if (Number(data.message?.ticketId) !== Number(ticketId)) return;

      if (data.action === "create") {
        if (filtrandoRef.current) return;
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    };
    socket.on("appMessage", onAppMessage);

    const onAppMessageReaction = (data) => {
      if (Number(data.ticketId) !== Number(ticketId)) return;

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    };
    socket.on("appMessageReaction", onAppMessageReaction);

    // ── Mensagens OTIMISTAS (frontend injeta sem esperar backend) ──
    const unsubscribeOptimistic = subscribeOptimistic((evt) => {
      if (!evt || !evt.tipo) return;
      if (
        evt.mensagem?.ticketId !== undefined &&
        Number(evt.mensagem.ticketId) !== Number(ticketId) &&
        Number(evt?.mensagem?.ticketId) !== Number(ticketId)
      ) {
        return;
      }
      if (evt.tipo === "ADD") {
        if (filtrandoRef.current) return;
        dispatch({ type: "ADD_MESSAGE", payload: evt.mensagem });
        scrollToBottom();
      } else if (evt.tipo === "UPDATE") {
        dispatch({ type: "UPDATE_MESSAGE", payload: evt.mensagem });
      } else if (evt.tipo === "REPLACE") {
        dispatch({
          type: "REPLACE_BY_OPTIMISTIC_ID",
          optimisticId: evt.optimisticId,
          payload: evt.mensagem,
        });
      } else if (evt.tipo === "REMOVE") {
        dispatch({ type: "REMOVE_MESSAGE", optimisticId: evt.optimisticId });
      }
    });

    return () => {
      // ── Cleanup: NÃO chamamos socket.disconnect() ──
      //
      // O socket é compartilhado singleton por todas as telas. Dar
      // `disconnect()` no unmount desta view parava a conexão das outras
      // telas abertas (atendimento, chats internos, configurações etc.) e
      // contribuía para o comportamento de "precisa F5 para atualizar o
      // visto".
      //
      // Alternativa correta:
      //   * Tirar os listeners que esta tela instalou (connect/appMessage/
      //     appMessageReaction) PASSANDO A MESMA REFERÊNCIA DE FUNÇÃO que
      //     foi usada no socket.on(). Sem isso (ex: socket.off só com nome
      //     do evento), a API Socket.IO v4 NÃO REMOVE NENHUM listener —
      //     cada troca de ticket acumulava 1 listener novo até o F5, e os
      //     handlers antigos (com ticketId do closure antigo) passavam a
      //     adicionar mensagens no ticket errado.
      //   * Sair da sala deste ticket (backend tem leaveChatBox handler).
      try { socket.off("connect", entrarNaSalaDoTicket); } catch (_) {}
      try { socket.off("appMessage", onAppMessage); } catch (_) {}
      try { socket.off("appMessageReaction", onAppMessageReaction); } catch (_) {}
      try { socket.emit("leaveChatBox", ticketId); } catch (_) {}
      try { document.removeEventListener("visibilitychange", aoVoltarVisivel); } catch (_) {}

      try {
        unsubscribeOptimistic && unsubscribeOptimistic();
      } catch (_e) {
        /* no-op */
      }
    };
  }, [ticketId, subscribeOptimistic]);

  // Busca dentro da conversa. Debounce para não disparar uma requisição por
  // tecla, e mínimo de 2 caracteres porque o backend recusa menos que isso.
  useEffect(() => {
    const termo = termoBusca.trim();

    if (termo.length < 2) {
      setAchados([]);
      setIndiceAchado(0);
      setBuscando(false);
      return undefined;
    }

    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/messages/${ticketId}/search`, {
          params: { q: termo },
        });

        if (currentTicketId.current !== ticketId) return;

        const encontrados = data.achados || [];

        setAchados(encontrados);
        setIndiceAchado(0);

        // Vai direto para o primeiro. Sem isto, com um resultado só não havia
        // como alcançá-lo: as setas só aparecem quando há mais de um.
        if (encontrados.length) {
          paginasPuxadasRef.current = 0;
          setAlvoBusca(encontrados[0].id);
        }
      } catch (err) {
        setAchados([]);
        toastError(err);
      }
      setBuscando(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [termoBusca, ticketId]);

  // Levar a conversa até a mensagem escolhida.
  //
  // Limitação assumida: a mensagem pode estar numa página que ainda não foi
  // carregada. Quando ela não está no DOM, o efeito pede mais uma página e se
  // repete quando a lista muda — ou seja, ele "rola para trás" sozinho até
  // achar. Isso tem custo (uma requisição por página, de 20 em 20), por isso o
  // teto do MAX_PAGINAS_ATRAS_DO_ALVO: passando disso a busca desiste e avisa,
  // em vez de puxar a conversa inteira em silêncio.
  useEffect(() => {
    if (!alvoBusca) return undefined;

    const elemento = document.getElementById(`mensagem-${alvoBusca}`);

    if (elemento) {
      elemento.scrollIntoView({ block: "center", behavior: "smooth" });
      setDestacada(alvoBusca);
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;

      const timer = setTimeout(() => setDestacada(null), 3000);
      return () => clearTimeout(timer);
    }

    if (loading) return undefined;

    if (!hasMore || paginasPuxadasRef.current >= MAX_PAGINAS_ATRAS_DO_ALVO) {
      toast.info(
        "Essa mensagem é antiga demais para ser aberta pela busca. Role a conversa para cima para carregá-la.",
      );
      setAlvoBusca(null);
      paginasPuxadasRef.current = 0;
      return undefined;
    }

    paginasPuxadasRef.current += 1;
    loadMore();

    return undefined;
  }, [alvoBusca, messagesList, hasMore, loading]);

  const irParaAchado = (indice) => {
    const achado = achados[indice];
    if (!achado) return;

    setIndiceAchado(indice);
    paginasPuxadasRef.current = 0;
    setAlvoBusca(achado.id);
  };

  const limparBusca = () => {
    setTermoBusca("");
    setAchados([]);
    setIndiceAchado(0);
    setAlvoBusca(null);
    setDestacada(null);
  };

  const reagir = async (message, emoji) => {
    try {
      const { data } = await api.post(`/messages/${message.id}/reaction`, {
        emoji,
      });

      dispatch({
        type: "SET_REACTION",
        payload: { messageId: data.messageId, reacoes: data.reacoes },
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  // Segura a conversa no fim enquanto o conteúdo ainda está crescendo.
  //
  // É o que conserta de verdade o "abre no meio": a rolagem inicial acontece
  // com imagem, vídeo e áudio ainda sem carregar, ocupando altura zero. Cada
  // um que termina de carregar empurra o conteúdo para baixo, e sem isto a
  // conversa fica parada onde estava — no meio.
  //
  // O ResizeObserver avisa a cada mudança de altura do conteúdo, venha ela de
  // mídia, de fonte ou do redimensionamento da janela. A trava `grudarNoFim`
  // é o que impede o efeito oposto: quem subiu para ler o histórico não pode
  // ser arrastado de volta a cada anexo que carrega.
  useEffect(() => {
    const lista = document.getElementById("messagesList");
    const conteudo = lista?.firstElementChild;

    if (!lista || !conteudo) return undefined;

    const observador = new ResizeObserver(() => {
      if (grudarNoFim.current) lista.scrollTop = lista.scrollHeight;
    });

    observador.observe(conteudo);

    return () => observador.disconnect();
  }, [ticketId]);

  // Trocar de atendimento sempre abre no fim, mesmo que a conversa anterior
  // tenha ficado parada no meio do histórico.
  useEffect(() => {
    grudarNoFim.current = true;
  }, [ticketId]);

  // Vai para o fim da conversa.
  //
  // Era `lastMessageRef.scrollIntoView({})`, e havia dois problemas. O
  // primeiro: sem `block`, o padrão é "start" — o navegador alinha a âncora ao
  // TOPO da área visível, não ao fim. O segundo, o que fazia a conversa abrir
  // no meio: a rolagem acontece assim que as mensagens entram na lista, mas
  // imagem, vídeo e áudio ainda não carregaram e ocupam altura zero. Quando
  // carregam, o conteúdo cresce POR BAIXO do ponto onde paramos, e a conversa
  // fica parada no meio. Numa conversa cheia de mídia — as do Instagram, por
  // exemplo — é sempre assim.
  //
  // Mexer no `scrollTop` do contêiner é exato e não depende de âncora nenhuma;
  // o `grudarNoFim` abaixo é o que segura a posição enquanto a mídia carrega.
  const scrollToBottom = () => {
    const lista = document.getElementById("messagesList");

    if (lista) lista.scrollTop = lista.scrollHeight;
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // Uma folga de 80px: quem está "quase" no fim continua sendo levado junto
    // pela mensagem nova, como em qualquer mensageiro.
    grudarNoFim.current = scrollHeight - scrollTop - clientHeight < 80;

    if (!hasMore) return;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  // Dois cliques na bolha respondem aquela mensagem.
  //
  // Responder citando já existia, mas escondido atrás da setinha que só aparece
  // ao passar o mouse: eram três passos (achar a seta, abrir o menu, escolher
  // "Responder") para o gesto mais comum de uma conversa. O duplo clique é o
  // mesmo atalho do WhatsApp Web.
  const responderMensagem = (message) => {
    if (modoSelecao || modoEncaminharLote || message.isDeleted || message.isInternal)
      return;

    // O duplo clique seleciona a palavra sob o cursor; sem limpar, fica um
    // trecho azulado no meio da conversa sem motivo nenhum.
    window.getSelection()?.removeAllRanges();

    setReplyingMessage(message);
  };

  // Ir até a mensagem citada — reaproveita o caminho da busca, que sabe puxar
  // páginas para trás quando a mensagem ainda não foi carregada.
  const irParaMensagem = (id) => {
    if (!id) return;

    paginasPuxadasRef.current = 0;
    setAlvoBusca(id);
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    const el = e.currentTarget;
    // CONGELA a posição no exato momento do clique. Mesmo que o botão depois
    // suma da tela (porque o ponteiro saiu do hover e group-hover desligou o
    // display:flex), as coordenadas que a gente usa para desenhar o menu são
    // as de quando estava tudo certo.
    const r = el.getBoundingClientRect();
    setAnchorRect({
      top: r.top,
      left: r.left,
      right: r.right,
      bottom: r.bottom,
      width: r.width,
      height: r.height
    });
    setAnchorEl(el);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
    setAnchorRect(null);
  };

  const alternarFavorito = async (message) => {
    const jaEra = favoritos.has(message.id);

    try {
      if (jaEra) {
        await api.delete(`/messages/${message.id}/favorite`);
      } else {
        await api.post(`/messages/${message.id}/favorite`);
      }

      setFavoritos((anterior) => {
        const novo = new Set(anterior);
        if (jaEra) novo.delete(message.id);
        else novo.add(message.id);
        return novo;
      });

      // Desfavoritar enquanto se olha só as favoritas: a mensagem sai da
      // lista na hora, senão fica ali contrariando o filtro.
      if (jaEra && apenasFavoritas) {
        dispatch({ type: "REMOVE_MESSAGE", payload: message.id });
      }
    } catch (err) {
      toastError(err);
    }
  };

  const renderSeloEditada = (message) => {
    if (!message?.editedAt) return null;
    return (
      <button
        type="button"
        onClick={() => {
          setMensagemEmEdicao(message);
          setEditHistoryOpen(true);
        }}
        className="inline-flex cursor-pointer items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
        title={
          message.previousBody
            ? `Antes: ${message.previousBody}`
            : "Mensagem editada"
        }
      >
        <Pencil className="h-2.5 w-2.5" />
        editada
      </button>
    );
  };

  // Estrela de favoritar. Só aparece ao passar o mouse — a não ser quando já
  // está marcada, que é justamente o que se quer enxergar rolando a conversa.
  const renderFavoritoBotao = (message) => {
    const favorita = favoritos.has(message.id);

    return (
      <button
        type="button"
        title={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-label={favorita ? "Remover dos favoritos" : "Favoritar mensagem"}
        aria-pressed={favorita}
        onClick={() => alternarFavorito(message)}
        className={cn(
          "absolute right-14 top-0 h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground hover:bg-black/5",
          favorita ? "flex text-amber-500" : "hidden group-hover:flex",
        )}
      >
        <Star className={cn("h-4 w-4", favorita && "fill-amber-400")} />
      </button>
    );
  };

  // Chips de reação, colados na base do balão. O total só aparece a partir de
  // dois: com uma pessoa só o número não informa nada e polui a conversa.
  const renderReacoes = (message) => {
    if (!message.reacoes?.length) return null;

    return (
      <div className="-mb-1 flex flex-wrap gap-1 pt-1">
        {message.reacoes.map((reacao) => (
          <button
            key={reacao.emoji}
            type="button"
            onClick={() => reagir(message, reacao.emoji)}
            title={
              reacao.euReagi ? "Remover sua reação" : "Reagir com este emoji"
            }
            aria-pressed={reacao.euReagi}
            className={cn(
              "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs leading-none transition-colors",
              reacao.euReagi
                ? "border-emerald-400 bg-emerald-100 dark:border-emerald-500 dark:bg-emerald-800/60"
                : "border-transparent bg-black/5 hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20",
            )}
          >
            <span>{reacao.emoji}</span>
            {reacao.total > 1 && (
              <span className="text-[10px] text-muted-foreground">
                {reacao.total}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────
  // [MENÇÕES] Substituição INVERSA: "@Numero" → "@Nome" no momento do
  // render (APENAS para exibição na UI do CRM; NÃO toca no body real
  // da mensagem que foi salvo no banco nem no whatsapp enviado).
  //
  // Por que a dupla replace doida? O WhatsApp exige corpo com dígitos
  // para linkar com o array mentions[] de JID e pintar a tag azul +
  // notificação. Dentro do CRM o atendente prefere ver o nome da pessoa
  // (o que ele de fato digitou no input).
  //
  // Faz o sort dos NÚMEROS MAIS LONGOS PRIMEIRO para não substituir
  // "551199876" dentro de "5511998765432".
  // ────────────────────────────────────────────────────────────────────
  const normalizarComparacaoRender = (s) =>
    String(s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const substituirMençõesRenderBonito = (textoOriginal) => {
    let saida = String(textoOriginal || "");
    if (!saida) return saida;
    if (!isGroup || !Array.isArray(participantes) || participantes.length === 0) {
      return saida;
    }
    const entradas = participantes
      .map((p) => ({
        numero: String(p?.numero || "").trim(),
        nome: String(p?.nome || "").trim(),
      }))
      .filter((x) => x.numero && x.nome);

    if (entradas.length === 0) return saida;

    // Números MAIS LONGOS primeiro (evita substituição parcial).
    entradas.sort((a, b) => b.numero.length - a.numero.length);

    entradas.forEach(({ numero, nome }) => {
      const numLimpo = String(numero || "").replace(/[^\d]/g, "");
      if (!numLimpo) return;

      // Caso 1: no corpo veio @+numero (ex "@5511998765432")
      const re1 = new RegExp(
        `(^|[\\s\u00A0])@${numLimpo}($|[\\s\u00A0.,;:!?()\\[\\]{}\\-])`,
        "ig",
      );
      saida = saida.replace(re1, (_m, antes, depois) => {
        return `${antes || ""}@${nome}${depois || ""}`;
      });

      // Caso 2: @numero grudado no final ou seguido de não-word.
      const re2 = new RegExp(`@${numLimpo}($|\\W)`, "ig");
      saida = saida.replace(re2, (_m, depois) => `@${nome}${depois || ""}`);
    });

    return saida;
  };

  // Numa mensagem de mídia o corpo é a legenda. Mensagens antigas guardavam o
  // nome do arquivo ali — nesse caso não há legenda de verdade para mostrar.
  //
  // Os rótulos padrão do backend ("🖼️ Imagem", "🎤 Áudio (0:25)", "🎥 Vídeo",
  // "🩹 Figurinha" etc.) NUNCA devem aparecer abaixo do componente visual da
  // mídia — causam duplicação (player/miniatura acima e texto repetido abaixo).
  const corpoVisivel = (message) => {
    // Localização: o cartão do mapa já mostra nome, endereço e coordenadas.
    if (message.mediaType === "location" && lerLocalizacao(message.body)) {
      return "";
    }

    if (!message.body) return "";

    // Rótulos padrão conhecidos gerados pelo backend em getMessageBody.
    // Qualquer match exato aqui → é rótulo automático, não é legenda do usuário.
    const bodyBruto = String(message.body || "").trim();
    // [MENÇÕES] Para EXIBIÇÃO no CRM: "@Numero" volta para "@Nome".
    // O body real do banco NÃO é alterado.
    const body = substituirMençõesRenderBonito(bodyBruto);

    if (message.mediaType === "image") {
      if (!message.mediaUrl) return substituirMençõesRenderBonito(message.body || "");
      // Legenda só é mostrada se NÃO for o rótulo padrão.
      if (body === "🖼️ Imagem") return "";
      return body;
    }

    if (message.mediaType === "audio") {
      return "";
    }

    if (message.mediaType === "video") {
      if (!message.mediaUrl) return substituirMençõesRenderBonito(message.body || "");
      if (/^(🎥 Vídeo|🎞️ GIF)(\s|$)/.test(body)) return "";
      return body;
    }

    if (message.mediaType === "sticker") {
      return "";
    }

    if (message.mediaType === "vcard" || message.mediaType === "contact") {
      return "";
    }

    // Para outros tipos (documents etc.): sem mediaUrl retorna body; se body
    // coincide com o nome do arquivo (mensagens antigas) também retorna vazio.
    if (!message.mediaUrl) return substituirMençõesRenderBonito(message.body || "");

    const arquivo = decodeURIComponent(message.mediaUrl.split("/").pop() || "");

    if (body === arquivo) return "";

    // Última proteção: se body começa com emoji do conjunto de rótulos padrão
    // e é um tipo com componente próprio, descarta por segurança.
    if (/^[🖼️🩹🎤🎵🎥🎞️📄📍]/.test(body)) {
      const tipo = (message.mediaType || "").toLowerCase();
      if (
        tipo === "image" ||
        tipo === "audio" ||
        tipo === "video" ||
        tipo === "sticker" ||
        tipo === "location" ||
        tipo === "vcard" ||
        tipo === "contact"
      ) {
        return "";
      }
    }

    return substituirMençõesRenderBonito(message.body || "");
  };

  // Anexos que estão à vista na conversa, do mais antigo para o mais novo — é
  // a ordem em que a conversa aconteceu, e é por ela que as setas do
  // visualizador andam.
  const arquivosDaConversa = messagesList
    .filter((m) => m.mediaUrl && !m.isDeleted)
    .map((m) => ({
      id: m.id,
      url: getPublicFileUrl(m.mediaUrl),
      mediaType: m.mediaType,
      legenda: corpoVisivel(m),
      fromMe: m.fromMe,
      createdAt: m.createdAt,
    }));

  const abrirArquivo = (messageId) => {
    const indice = arquivosDaConversa.findIndex((a) => a.id === messageId);
    if (indice >= 0) setVisualizando(indice);
  };

  const alternarSelecao = (messageId) => {
    setSelecionadas((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(messageId)) novo.delete(messageId);
      else novo.add(messageId);
      return novo;
    });
  };

  const todosArquivosMarcados =
    arquivosDaConversa.length > 0 &&
    arquivosDaConversa.every((a) => selecionadas.has(a.id));

  const alternarTodosDaConversa = () => {
    setSelecionadas(() =>
      todosArquivosMarcados
        ? new Set()
        : new Set(arquivosDaConversa.map((a) => a.id)),
    );
  };

  const sairDaSelecao = () => {
    setModoSelecao(false);
    setSelecionadas(new Set());
  };

  // `ids` vazio significa "tudo o que este atendimento tem" — o backend monta
  // o ZIP a partir do banco, e não do que já foi rolado na tela. Sem isso,
  // "Baixar tudo" numa conversa longa levaria só as mensagens carregadas.
  const baixarArquivos = async (ids, rotulo) => {
    setBaixando(rotulo);
    try {
      const { data, headers } = await api.post(
        `/messages/${ticketId}/gallery/download`,
        { messageIds: ids },
        { responseType: "blob" },
      );

      const nome =
        (headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] ||
        `arquivos-atendimento-${ticketId}.zip`;

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("O download do ZIP começou.");
      if (ids.length) sairDaSelecao();
    } catch (err) {
      // Com responseType blob a mensagem de erro também chega como blob.
      if (err?.response?.data instanceof Blob) {
        try {
          const texto = await err.response.data.text();
          err.response.data = JSON.parse(texto);
        } catch {
          // Fica o erro genérico mesmo.
        }
      }
      toastError(err);
    } finally {
      setBaixando(null);
    }
  };

  const alternarSelecaoEncaminhar = (messageId) => {
    setSelecionadasEncaminhar((anterior) => {
      const novo = new Set(anterior);
      if (novo.has(messageId)) novo.delete(messageId);
      else novo.add(messageId);
      return novo;
    });
  };

  const todasMensagensParaEncaminhar = messagesList.filter(
    (m) => !m.isDeleted && !m.isInternal,
  );

  const todasMarcadasParaEncaminhar =
    todasMensagensParaEncaminhar.length > 0 &&
    todasMensagensParaEncaminhar.every((m) =>
      selecionadasEncaminhar.has(m.id),
    );

  const alternarTodasEncaminhar = () => {
    setSelecionadasEncaminhar(() =>
      todasMarcadasParaEncaminhar
        ? new Set()
        : new Set(todasMensagensParaEncaminhar.map((m) => m.id)),
    );
  };

  const sairDaSelecaoEncaminhar = () => {
    setModoEncaminharLote(false);
    setSelecionadasEncaminhar(new Set());
  };

  const abrirEncaminhamentoLote = () => {
    if (!selecionadasEncaminhar.size) return;
    // Preserva a ordem cronológica: a lista do reducer está ordenada da mais
    // antiga para a mais nova. O ForwardMessageModal enviará em série nesta
    // mesma ordem, preservando a sequência da conversa no destino.
    const ordenadas = messagesList.filter(
      (m) => selecionadasEncaminhar.has(m.id),
    );
    setEncaminharLoteSelecionadas(ordenadas);
    setModalEncaminharAberto(true);
  };

  const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
  const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

  const IconeDoDocumento = ({ extensao }) => {
    if (PLANILHAS.includes(extensao))
      return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
    if (COMPACTADOS.includes(extensao))
      return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
    return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
  };

  // No modo seleção, uma capa transparente cobre a bolha inteira e recebe o
  // clique. Sem ela, clicar numa imagem abriria o lightbox e num vídeo daria
  // play — o clique nunca chegaria na seleção.
  //
  // Dois modos distintos de seleção convivem:
  //   - modoSelecao (baixar ZIP): só permite marcar mensagens que TÊM anexo.
  //   - modoEncaminharLote: permite marcar QUALQUER mensagem (texto, mídia,
  //     figurinha, localização, cartão etc.). A única exclusão são notas
  //     internas e mensagens apagadas.
  const renderCapaDeSelecao = (message) => {
    if (!modoSelecao && !modoEncaminharLote) return null;

    if (modoSelecao) {
      if (!message.mediaUrl) return null;
      const marcada = selecionadas.has(message.id);

      return (
        <button
          type="button"
          onClick={() => alternarSelecao(message.id)}
          aria-pressed={marcada}
          aria-label={marcada ? "Desmarcar arquivo" : "Marcar arquivo"}
          className="absolute inset-0 z-10 rounded-2xl"
        >
          <span
            className={cn(
              "absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-background/90",
              marcada
                ? "border-primary bg-primary text-primary-foreground"
                : "border-muted-foreground/50",
            )}
          >
            {marcada && <Check className="h-3 w-3" />}
          </span>
        </button>
      );
    }

    if (modoEncaminharLote) {
      if (message.isDeleted || message.isInternal) return null;

      const marcada = selecionadasEncaminhar.has(message.id);
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            alternarSelecaoEncaminhar(message.id);
          }}
          onDoubleClick={(e) => e.stopPropagation()}
          aria-pressed={marcada}
          aria-label={
            marcada ? "Desmarcar para encaminhar" : "Marcar para encaminhar"
          }
          className="absolute inset-0 z-10 rounded-2xl"
        >
          <span
            className={cn(
              "absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 bg-background/95 backdrop-blur-sm",
              marcada
                ? "border-primary bg-primary text-primary-foreground"
                : "border-muted-foreground/50 group-hover:border-muted-foreground/80",
            )}
          >
            {marcada && <Check className="h-3 w-3" />}
          </span>
        </button>
      );
    }

    return null;
  };

  // Classes que a bolha ganha enquanto se escolhe arquivo ou encaminha em
  // lote.
  const classesDeSelecao = (message) => {
    if (!modoSelecao && !modoEncaminharLote) return "";

    if (modoSelecao) {
      return message.mediaUrl
        ? cn(
            "cursor-pointer",
            selecionadas.has(message.id) &&
              "ring-2 ring-primary ring-offset-1",
          )
        : "opacity-50";
    }

    if (modoEncaminharLote) {
      if (message.isDeleted || message.isInternal) {
        return "opacity-40 pointer-events-none";
      }
      return cn(
        "cursor-pointer",
        selecionadasEncaminhar.has(message.id) &&
          "ring-2 ring-primary ring-offset-1",
      );
    }

    return "";
  };

  const adicionarAosContatos = async (contato) => {
    setAdicionando(contato.id);

    try {
      await api.put(`/contacts/${contato.id}/agenda`);
      setAdicionados((anteriores) => new Set(anteriores).add(contato.id));
      toast.success(`${contato.name} agora está nos seus contatos.`);
    } catch (err) {
      toastError(err);
    }

    setAdicionando(null);
  };

  // Quem falou no grupo — com a marca de "já está nos meus contatos".
  //
  // Todo participante vira contato no banco para a mensagem ter dono, então
  // "existe em Contacts" não responde nada. O que a equipe precisa saber é se
  // aquela pessoa é conhecida da empresa ou um número qualquer que apareceu
  // no grupo; quando não é, o botão adiciona sem tirar ninguém da conversa.
  const renderAutorDoGrupo = (message) => {
    const contato = message.contact;

    if (!contato) return null;

    const naAgenda = contato.savedInAgenda || adicionados.has(contato.id);

    return (
      <span className="flex items-center gap-1 font-medium text-sky-500">
        {contato.name}
        {naAgenda ? (
          <BadgeCheck
            className="h-3.5 w-3.5 text-emerald-500"
            title="Já está nos seus contatos"
          />
        ) : (
          <button
            type="button"
            onClick={() => adicionarAosContatos(contato)}
            disabled={adicionando === contato.id}
            title="Adicionar aos contatos"
            aria-label={`Adicionar ${contato.name} aos contatos`}
            className="flex items-center gap-0.5 rounded-full border border-dashed px-1.5 text-[10px] font-normal text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <UserPlus className="h-3 w-3" />
            adicionar
          </button>
        )}
      </span>
    );
  };

  const checkMessageMedia = (message) => {
    const urlMedia = getPublicFileUrl(message.mediaUrl);

    if (message.mediaType === "location") {
      // A leitura do corpo mora em utils/localizacao: são três formatos de
      // gravação convivendo na mesma conversa (o atual, o antigo de duas
      // partes e o do Telegram, sem separador nenhum).
      const local = lerLocalizacao(message.body);

      if (local) {
        return (
          <LocationPreview
            image={local.imagem}
            link={local.link}
            description={local.descricao}
            latitude={local.latitude}
            longitude={local.longitude}
          />
        );
      }
    }

    if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />;
    } else if (message.mediaType === "sticker") {
      if (!urlMedia) {
        return (
          <div
            className="flex h-[150px] w-[150px] items-center justify-center rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
            title="Figurinha não carregada"
          >
            Figurinha
          </div>
        );
      }
      // ── Figurinha: <img> CRU (sem ModalImageCors, sem onClick) ─────────
      // Razão do bug anterior "parou de se mover" + "não salva porque
      // quando clica abre":
      //   - <ModalImageCors> carregava miniaturaDe → estática 1 frame.
      //   - Wrapper clicável capturava evento, bloqueava menu contexto.
      //   - Usuário quer "igual antes": só desenha, toca, NÃO abre nada.
      //
      // Usa getPublicFileUrl DIRETO, ORIGINAL (sem miniaturaDe).
      // <img> nativo toca GIF/WebP/APNG animados automaticamente em loop.
      // draggable=true + WebkitUserDrag=auto + sem onClick: clique direito
      // mostra "Salvar imagem como..." normalmente.
      // Tamanho: 150px × 150px (era 130). Aumentado sem mexer no padding
      // pb-5 do wrapper e sem mudar posições dos botões de menu right-7
      // e favorito right-14 (ambos absolute top-0 bg-inherit transparente
      // → ficam flutuando sobre a área da figurinha, sem conflito).
      return (
        <img
          src={urlMedia}
          alt="Figurinha"
          loading="eager"
          decoding="async"
          draggable
          onContextMenu={(e) => {
            // Garante que bubble não chegue a wrappers com onClick.
            e.stopPropagation();
            // Sem preventDefault → menu nativo aparece.
          }}
          style={{ WebkitUserDrag: "auto", userSelect: "auto" }}
          className="h-[150px] w-[150px] object-contain"
        />
      );
    } else if (message.mediaType === "image") {
      // Antes havia também uma checagem de extensão (jpe?g|png|gif). Ela era
      // redundante — o backend já deriva mediaType do mimetype — e deixava de
      // fora tudo o mais que o WhatsApp manda: .webp (o formato padrão de
      // figurinha e de foto encaminhada), .heic do iPhone, .bmp, .avif. O que
      // não casava virava botão de download, e a conversa ficava com um
      // "Download" no lugar da imagem.
      return <ModalImageCors imageUrl={urlMedia} />;
    } else if (message.mediaType === "audio") {
      return (
        <>
          <Audio url={urlMedia} />
          <TranscricaoDeAudio message={message} />
        </>
      );
    } else if (message.mediaType === "video") {
      // IMPORTANTE: detectar extensão na URL JÁ PROCESSADA (urlMedia com
      // getPublicFileUrl) e não no valor bruto message.mediaUrl do backend.
      // getPublicFileUrl pode converter para proxy, caminho relativo ou URL
      // assinada alterando o pathname; detectar na URL bruta gerava falso
      // negativo e GIFs animados caíam no componente de vídeo (que não toca
      // arquivos .gif).
      const extensao = extensaoDe(urlMedia || message.mediaUrl || "");
      const extLower = extensao ? extensao.toLowerCase() : "";
      // Formatos de imagem animada que chegam marcados como "video" mas a
      // tag <video> não reproduz no navegador: desenhar como imagem.
      const ehImagemAnimada = [".gif", ".webp", ".apng", ".avif"].includes(extLower);
      if (ehImagemAnimada) {
        return <ModalImageCors imageUrl={urlMedia} />;
      }
      // Vídeos normais (mp4/webm/mov etc.) → componente com player e capa.
      return <VideoNaConversa url={urlMedia} />;
    } else {
      // Documento. Antes era só um botão "Download": para saber se o PDF era
      // o contrato certo, a pessoa baixava, abria fora e voltava. Agora o
      // cartão diz o que é o arquivo e abre no visualizador, sem sair daqui.
      const extensao = extensaoDe(message.mediaUrl);
      const rotulo = extensao
        ? `Arquivo ${extensao.replace(".", "").toUpperCase()}`
        : "Arquivo";

      return (
        <div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
          {/* PDF mostra a primeira página; os outros formatos não têm como
              ser desenhados pelo navegador e ficam só no cartão. */}
          {extensao === ".pdf" && (
            <div className="mb-2">
              <MiniaturaDePdf
                url={urlMedia}
                onAbrir={() => abrirArquivo(message.id)}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
          <IconeDoDocumento extensao={extensao} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{rotulo}</p>
            <p className="text-xs text-muted-foreground">
              {extensao ? extensao.replace(".", "").toUpperCase() : "documento"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            title="Visualizar"
            onClick={() => abrirArquivo(message.id)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0">
            <a
              href={urlMedia}
              target="_blank"
              rel="noopener noreferrer"
              title="Baixar"
            >
              <Download className="h-4 w-4" />
            </a>
          </Button>
          </div>
        </div>
      );
    }
  };

  const resolverAutorMensagem = (message, index) => {
    if (!message?.fromMe) return null;
    // 1. Mensagem enviada pelo FlowBuilder: mostra "Fluxo · NomeDoFluxo"
    //    na frente de qualquer outra regra. Usa flowId como id para o
    //    agrupamento não repetir nome em mensagens consecutivas do mesmo
    //    fluxo.
    if (message?.flowId || message?.flowName) {
      const nomeFluxo = message.flowName || "Fluxo";
      return {
        id: Number(message.flowId) || 0,
        name: `Fluxo · ${String(nomeFluxo).trim()}`.trim(),
      };
    }
    if (message?.user?.id && message?.user?.name) return message.user;
    // Heurística para mensagens antigas sem userId no banco (antes da
    // migração): olhar a vizinhança no mesmo minuto (atrás ou à frente) e
    // herdar o autor se ele vier de fromMe=true também.
    for (let passo = 1; passo <= 5; passo++) {
      const antes = messagesList[index - passo];
      const depois = messagesList[index + passo];
      for (const v of [antes, depois]) {
        if (
          v &&
          v.fromMe &&
          v.user?.id &&
          v.user?.name &&
          v.createdAt &&
          message.createdAt &&
          Math.abs(
            parseDataSegura(v.createdAt).getTime() - parseDataSegura(message.createdAt).getTime(),
          ) <= 10 * 60 * 1000
        ) {
          return v.user;
        }
      }
    }
    // Se é a própria pessoa logada mandando, usa o nome dela (evita F5 e
    // resolve mensagens novas que ainda não chegaram com user preenchido).
    if (
      usuarioLogado &&
      usuarioLogado.id &&
      usuarioLogado.name &&
      (!message.body || !temAssinaturaNoCorpo({ ...message, user: usuarioLogado }))
    ) {
      return { id: usuarioLogado.id, name: usuarioLogado.name };
    }
    return message?.user || null;
  };

  const temAssinaturaNoCorpo = (message) => {
    if (!message || !message.body) return false;
    // Mensagens do FlowBuilder NUNCA tem assinatura no corpo (o robô não
    // assina o nome no texto que vai para o cliente). Forçar false aqui
    // evita que o nome "Fluxo · X" seja interpretado como assinatura e
    // acabe ocultando o próprio nome no rodapé.
    if (message?.flowId || message?.flowName) return false;
    const user = resolverAutorMensagem(message, messagesList.indexOf(message));
    if (!user?.name) return false;
    const trimmed = String(message.body).trimStart();
    const nomesParaTestar = new Set(
      [user.name]
        .filter(Boolean)
        .flatMap((n) => {
          const t = String(n).trim();
          if (!t) return [];
          return [
            `*${t}:*`,
            `*${t} :*`,
            `${t}:`,
            `*${t.split(" ")[0]}:*`,
            `${t.split(" ")[0]}:`,
          ].map((x) => x.toLowerCase());
        }),
    );
    const comeco = trimmed.split("\n")[0]?.toLowerCase() || "";
    for (const padrao of nomesParaTestar) {
      if (comeco.startsWith(padrao)) return true;
    }
    return false;
  };

  const mostrarNomeAutorEquipe = (message, index) => {
    if (showAttendantNameInBubble !== true) return false;
    if (!message.fromMe) return false;
    const autor = resolverAutorMensagem(message, index);
    if (!autor?.name) return false;
    // Assinatura no corpo só bloqueia nome do ATENDENTE; mensagens do fluxo
    // mostram nome sempre (a menos que o setting global esteja desligado).
    const ehMensagemFluxo = Boolean(message?.flowId || message?.flowName);
    if (!ehMensagemFluxo && temAssinaturaNoCorpo(message)) return false;

    if (index === 0) return true;

    const anterior = messagesList[index - 1];
    if (!anterior?.fromMe) return true;
    const autorAnterior = resolverAutorMensagem(anterior, index - 1);
    if ((autorAnterior?.id || null) !== (autor.id || null)) return true;
    if (!isSameDay(parseDataSegura(anterior.createdAt), parseDataSegura(message.createdAt))) {
      return true;
    }
    return false;
  };

  const foiEnviadoDoAparelho = (message) => {
    if (!message?.fromMe) return false;
    if (message.sentFromDevice === true) return true;
    return false;
  };

  const renderAutorEquipeInline = (message, index) => {
    if (showAttendantNameInBubble !== true) return null;
    if (!message.fromMe) return null;
    const idxReal = typeof index === "number" ? index : messagesList.indexOf(message);
    if (!mostrarNomeAutorEquipe(message, idxReal)) return null;
    const autor = resolverAutorMensagem(message, idxReal);
    if (!autor?.name) return null;
    return (
      <>
        <span className="mr-1 text-muted-foreground">{autor.name.trim()}</span>
        <span className="mr-1 text-muted-foreground/70">·</span>
      </>
    );
  };

  const renderMessageAck = (message) => {
    if (message.isOptimistic === true) {
      return <Loader2 className="ml-1 inline h-3.5 w-3.5 align-middle animate-spin text-muted-foreground/70" />;
    }
    if (message.ack === 0) {
      return <Clock className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 1) {
      return <Check className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 2) {
      return <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle" />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <CheckCheck className="ml-1 inline h-3.5 w-3.5 align-middle text-emerald-500" />
      );
    }
  };

  // "Hoje" e "Ontem" poupam quem lê de converter a data mentalmente; só a
  // partir de anteontem o número passa a ser mais informativo que o rótulo.
  //
  // (Usamos o helper seguro `rotuloDoDiaSeguro` para não crashar se o
  // backend enviar uma atualização de mensagem sem `createdAt` preenchido.)
  const rotuloDoDia = (data) => rotuloDoDiaSeguro(data);

  const renderDailyTimestamps = (message, index) => {
    const ultima = index === messagesList.length - 1;
    const ehNovaData =
      index === 0 ||
      !isSameDay(
        parseDataSegura(messagesList[index]?.createdAt),
        parseDataSegura(messagesList[index - 1]?.createdAt)
      );

    const dataDoDia = ehNovaData ? (
      <div
        key={`timestamp-${message.id}`}
        id={`date-marker-${index}`}
        data-dia={rotuloDoDiaSeguro(messagesList[index]?.createdAt)}
        className="mx-auto mt-4 mb-2 w-fit self-center rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-sm"
      >
        {rotuloDoDiaSeguro(messagesList[index]?.createdAt)}
      </div>
    ) : null;

    if (ultima) {
      return (
        <React.Fragment key={`wrap-last-${message.id}`}>
          {dataDoDia}
          <div
            key={`ref-${message.createdAt}`}
            ref={lastMessageRef}
            style={{ float: "left", clear: "both" }}
          />
        </React.Fragment>
      );
    }

    return dataDoDia;
  };

  // A faixa que separa um atendimento do outro.
  //
  // A conversa traz o histórico do contato, e sem esta marca as falas de três
  // chamados diferentes viravam um bloco só: ninguém distinguia o que foi dito
  // no problema de ontem do que está sendo tratado agora. Traz o número e a
  // fila, que é como a equipe se refere ao chamado.
  const renderFaixaDoAtendimento = (message, index) => {
    const anterior = messagesList[index - 1];

    if (anterior && anterior.ticketId === message.ticketId) return null;

    const dados = atendimentos[message.ticketId];
    const numero = message.ticketId;
    const fila = dados?.fila;
    const atual = Number(ticketId) === Number(numero);

    return (
      <div
        key={`atendimento-${numero}-${message.id}`}
        className="my-3 flex items-center gap-2 self-stretch"
      >
        <span className="h-px flex-1 bg-border" />
        <span
          className={cn(
            "w-fit rounded-full px-3 py-1 text-[11px] font-medium shadow-xs",
            atual
              ? "bg-primary text-primary-foreground"
              : "border bg-background/85 text-muted-foreground backdrop-blur-sm",
          )}
          title={
            dados?.status === "closed"
              ? "Atendimento encerrado"
              : "Atendimento em andamento"
          }
        >
          #Atendimento {numero}
          {fila ? ` — ${fila.toUpperCase()}` : ""}
          {dados?.status === "closed" ? " · encerrado" : ""}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    );
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return <span className="mt-4" key={`divider-${message.id}`}></span>;
      }
    }
  };

  const MiniImagemQuote = ({ src }) => {
    const [erro, setErro] = useState(false);
    if (erro || !src) {
      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md bg-black/10">
          <ImageIcon className="h-5 w-5 text-muted-foreground/60" />
        </div>
      );
    }
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-12 w-12 shrink-0 rounded-l-md object-cover"
        onError={() => setErro(true)}
      />
    );
  };

  const MiniStickerQuote = ({ src }) => {
    const [erro, setErro] = useState(false);
    if (erro || !src) {
      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md" style={{ background: "transparent" }}>
          <Sticker className="h-5 w-5 text-muted-foreground/70" />
        </div>
      );
    }
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="h-12 w-12 shrink-0 rounded-l-md object-contain"
        style={{ background: "transparent" }}
        onError={() => setErro(true)}
      />
    );
  };

  const renderMediaQuoteThumb = (quoted) => {
    if (!quoted) return null;
    const urlMedia = quoted.mediaUrl ? getPublicFileUrl(quoted.mediaUrl) : "";
    const tipo = quoted.mediaType || "";
    const ext = urlMedia ? extensaoDe(urlMedia) : "";
    const ehAudio = tipo === "audio";
    const ehVideo = tipo === "video" || [".mp4", ".webm", ".mov", ".m4v", ".ogv", ".3gp", ".mkv", ".avi"].includes(ext);
    const ehSticker = tipo === "sticker";
    const ehImage = tipo === "image" || [".jpg", ".jpeg", ".jfif", ".png", ".gif", ".webp", ".bmp", ".heic", ".heif", ".tiff", ".avif"].includes(ext);
    const ehDoc = tipo === "document" || (!ehAudio && !ehVideo && !ehSticker && !ehImage && quoted.mediaUrl);
    const ehLocation = tipo === "location";
    const ehVcard = tipo === "vcard" || tipo === "contact";

    const iconWrapper = (children, cor) => (
      <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-l-md", cor)}>
        {children}
      </div>
    );

    // ── WhatsApp Web: áudio NAO TEM miniatura colorida grande ──
    //    Usa só um ícone MIC pequeno cinza INLINE ao lado da duração.
    //    (igual a 2ª imagem enviada: "Amor ❤️" acima + "🎤 0:02" ao lado)
    if (ehAudio) return null;

    if (ehSticker && urlMedia) {
      return <MiniStickerQuote src={urlMedia} />;
    }
    if (ehImage && urlMedia) {
      return <MiniImagemQuote src={urlMedia} />;
    }
    if (ehVideo && urlMedia) {
      return iconWrapper(<VideoIcon className="h-5 w-5 text-white" />, "bg-black/70");
    }
    if (ehLocation) {
      return iconWrapper(<MapPin className="h-5 w-5 text-white" />, "bg-red-500/80");
    }
    if (ehVcard) {
      return iconWrapper(<UserRound className="h-5 w-5 text-white" />, "bg-sky-500/80");
    }
    if (ehDoc) {
      if (ext === ".pdf") return iconWrapper(<FileText className="h-5 w-5 text-white" />, "bg-red-500/80");
      if ([".xls", ".xlsx", ".csv"].includes(ext)) return iconWrapper(<FileSpreadsheet className="h-5 w-5 text-white" />, "bg-emerald-500/80");
      if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(ext)) return iconWrapper(<FileArchive className="h-5 w-5 text-white" />, "bg-amber-500/80");
      return iconWrapper(<FileIcon className="h-5 w-5 text-white" />, "bg-slate-500/80");
    }
    return null;
  };

  const renderQuotedMessage = (message) => {
    const quoted = message.quotedMsg;
    if (!quoted) return null;
    const thumb = renderMediaQuoteThumb(quoted);
    const temThumb = Boolean(thumb);
    const temMidiaAbaixo = Boolean(
      message.mediaType === "image" ||
      message.mediaType === "audio" ||
      message.mediaType === "video" ||
      message.mediaType === "document" ||
      message.mediaType === "sticker" ||
      message.mediaType === "location" ||
      message.mediaType === "vcard" ||
      message.mediaType === "contact" ||
      message.mediaUrl
    );
    const tipo = quoted.mediaType || "";
    const ehAudio = tipo === "audio";

    // WhatsApp Web: áudio citado = MIC pequeno cinza + duração inline,
    // NÃO usa miniatura colorida grande (consistente com a 2ª imagem).
    let prefixoConteudo = null;
    let corpoTexto = quoted.body;
    if (ehAudio) {
      const duracaoSeg = Number(quoted.duration || 0) || 0;
      const mm = Math.floor(duracaoSeg / 60);
      const ss = String(duracaoSeg % 60).padStart(2, "0");
      prefixoConteudo = (
        <span className="mr-1.5 inline-flex items-center gap-1.5 text-muted-foreground">
          <Mic className="h-3.5 w-3.5" />
          <span>{mm}:{ss}</span>
        </span>
      );
      corpoTexto = corpoTexto || "";
    }

    return (
      <div
        role="button"
        tabIndex={0}
        title="Ir para a mensagem citada"
        onClick={() => irParaMensagem(quoted.id)}
        onKeyDown={(e) => e.key === "Enter" && irParaMensagem(quoted.id)}
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(
          // WhatsApp: citação + mídia abaixo formam "uma peça só" (grudadas).
          "-mx-1.5 -my-0.5 flex cursor-pointer overflow-hidden rounded-lg text-left hover:brightness-95",
          temMidiaAbaixo ? "mb-0 rounded-b-none" : "mb-1.5",
          message.fromMe ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted",
        )}
      >
        <span
          className={cn(
            "w-1 shrink-0",
            quoted.fromMe ? "bg-emerald-400" : "bg-sky-400",
          )}
        />
        {thumb}
        <div className={cn("min-w-0 flex-1 overflow-hidden whitespace-pre-wrap", temThumb ? "p-2.5" : "p-2.5")}>
          <div className="mb-0.5 flex items-baseline gap-2">
            {!quoted.fromMe && (
              <span className="font-medium text-sky-500">
                {quoted.contact?.name}
              </span>
            )}
            {prefixoConteudo}
          </div>
          {corpoTexto && (
            <div className="line-clamp-2 break-words text-xs sm:text-[13px] text-muted-foreground/90">
              {corpoTexto}
            </div>
          )}
          {!corpoTexto && ehAudio && !prefixoConteudo && (
            <div className="text-xs text-muted-foreground">Áudio</div>
          )}
        </div>
      </div>
    );
  };

  // O canto reto de um lado só é o que dá a "ponta" da bolha e diz de quem
  // é a fala sem precisar de cor forte. Sombra fica quase imperceptível: com
  // centenas de bolhas na tela, sombra pesada vira ruído.
  const bubbleBase =
    "relative mt-0.5 block h-auto max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-2xl px-3 pb-1.5 pt-2 shadow-[0_1px_2px_rgba(0,0,0,0.06)] sm:max-w-[600px]";

  const ehFigurinha = (message) =>
    message.mediaType === "sticker" && !!message.mediaUrl && !message.isDeleted;

  const ehMensagemOtimista = (message) => Boolean(message?.isOptimistic);

  // Figurinha fica fora do balão: no WhatsApp ela flutua no fundo da conversa,
  // e é isso que a distingue de um print que alguém mandou.
  // Otimistas (enviando) ficam um pouco opacos para não confundir com confirmado.
  const classesDeFigurinha = (message) =>
    ehFigurinha(message)
      ? `bg-transparent px-0 pb-5 pt-0 shadow-none dark:bg-transparent ${
          ehMensagemOtimista(message) ? "opacity-65 will-change-transform" : ""
        }`
      : "";

  // Sem balão atrás, a hora sumiria em cima do desenho: ganha a mesma pílula
  // escura que o WhatsApp usa.
  const classesDaHora = (message) =>
    ehFigurinha(message) ? "rounded bg-black/50 px-1 py-px text-white" : "";

  const [dataStickyTexto, setDataStickyTexto] = React.useState("");

  React.useEffect(() => {
    if (messagesList.length === 0) {
      setDataStickyTexto("");
      return;
    }

    const scrollContainer = document.getElementById("messagesList");
    const seletores = '[id^="date-marker-"]';

    const atualizaSticky = () => {
      const marcadores = Array.from(
        scrollContainer?.querySelectorAll(seletores) || []
      );
      if (!marcadores.length) {
        setDataStickyTexto("");
        return;
      }
      const topoRef = scrollContainer?.getBoundingClientRect().top || 0;
      let ultimoPassou = null;
      for (const m of marcadores) {
        const rect = m.getBoundingClientRect();
        if (rect.top - topoRef <= 60) {
          ultimoPassou = m.getAttribute("data-dia") || "";
        } else {
          break;
        }
      }
      if (ultimoPassou !== null) {
        setDataStickyTexto(ultimoPassou);
      } else {
        setDataStickyTexto(marcadores[0].getAttribute("data-dia") || "");
      }
    };

    atualizaSticky();

    const obs = new IntersectionObserver(
      () => atualizaSticky(),
      {
        root: scrollContainer,
        threshold: [0, 0.01, 0.5, 0.99, 1],
        rootMargin: "-10px 0px -90% 0px",
      }
    );

    const t1 = setTimeout(() => {
      const ms = Array.from(
        scrollContainer?.querySelectorAll(seletores) || []
      );
      ms.forEach((m) => obs.observe(m));
      atualizaSticky();
    }, 50);

    scrollContainer?.addEventListener("scroll", atualizaSticky, {
      passive: true,
    });
    window.addEventListener("resize", atualizaSticky);

    return () => {
      clearTimeout(t1);
      obs.disconnect();
      scrollContainer?.removeEventListener("scroll", atualizaSticky);
      window.removeEventListener("resize", atualizaSticky);
    };
  }, [messagesList.length]);

  const renderMessages = () => {
    if (messagesList.length === 0 && !filtrando) {
      return <div>Say hello to your new contact!</div>;
    }

    if (filtrando && !loading && messagesList.length === 0) {
      return (
        <div className="m-auto rounded-lg border border-dashed bg-background/70 px-4 py-3 text-center text-sm text-muted-foreground">
          {apenasFavoritas
            ? "Nenhuma mensagem favoritada neste recorte."
            : "Nenhuma mensagem no período escolhido."}
        </div>
      );
    }

    const viewMessagesList = messagesList.map((message, index) => {
      // Chave ÚNICA hierárquica — garante NÃO haver 2 Fragment/React.Fragment
      // com a mesma key, nem em stickers (que costumavam chegar 2x na lista
      // com o mesmo id do WhatsApp e davam "Encountered two children with the
      // same key 3EB0…"). Ordem:
      //   1) id definitivo do banco/WhatsApp (existe em 99,9% após ADD real)
      //   2) optimisticId temporário (enquanto a otimista ainda não foi
      //      substituída pela real — tipicamente dura < 500ms)
      //   3) fallback index + hash do conteúdo, para o caso de um payload
      //      incompleto chegar sem nenhum id (webhook estranho).
      let key = null;
      if (message?.id !== undefined && message?.id !== null && String(message.id).length > 0) {
        key = `id-${String(message.id)}`;
      } else if (message?.optimisticId) {
        key = `opt-${String(message.optimisticId)}`;
      } else {
        // Fallback baratinho (não colisão quase impossível dentro da mesma
        // lista): fingerprint do conteúdo + timestamp + posição.
        const raw = [
          message?.mediaType ?? "",
          message?.body ?? "",
          message?.mediaUrl ?? "",
          message?.createdAt ?? "",
          String(index),
        ].join("|");
        let h = 0;
        for (let i = 0; i < raw.length; i++) { h = (h * 31 + raw.charCodeAt(i)) | 0; }
        key = `fb-${index}-${h >>> 0}`;
      }

      if (message.isInternal) {
        const autor = message.user || null;
        const idAutor =
          Number(message.userId) ||
          Number(autor?.id) ||
          null;

        // Resolver nome REAL do autor: user (associação do backend) > usersEquipe local > "Usuário".
        // Removido fallback "Equipe" genérico que aparecia no rodapé da primeira imagem.
        let nomeAutor = autor?.name ? autor.name.trim() : "";
        if (!nomeAutor && usersEquipe.length > 0 && idAutor) {
          const local = usersEquipe.find((u) => +u.id === +idAutor);
          if (local?.name) nomeAutor = String(local.name).trim();
        }
        if (!nomeAutor) nomeAutor = "Usuário";

        const idEu = Number(usuarioLogado?.id) || null;
        const ehMinhaNota = idAutor && idEu && idAutor === idEu;

        // AGRUPAMENTO SEQUENCIAL (padrão WhatsApp, igual 2ª imagem do usuário):
        // - NOTA ASSINADA (1ª do grupo): mensagem anterior NÃO é interna OU internamente mas AUTOR DIFERENTE.
        // - NOTA SEM ASSINATURA (contínua da mesma pessoa): mensagem anterior É interna E MESMO userId.
        // Desta forma: 1ª do bloco mostra cabeçalho "Autor:", as próximas na sequência são menores, sem repetir o nome.
        const anterior = index > 0 ? messagesList[index - 1] : null;
        const idAutorAnteriorInterno =
          anterior?.isInternal
            ? Number(anterior.userId) || Number(anterior.user?.id) || null
            : null;
        const continuaMesmoAutor = Boolean(
          anterior?.isInternal &&
            idAutor &&
            idAutorAnteriorInterno &&
            idAutor === idAutorAnteriorInterno,
        );

        const dataMsg = parseDataSegura(message.createdAt);
        const idsMencionados = Array.isArray(message.internalMentions)
          ? message.internalMentions.filter((id) => Number.isInteger(id))
          : [];
        const mencionados = idsMencionados
          .map((id) => usersEquipe.find((u) => +u.id === +id))
          .filter(Boolean);

        // Detecção de MENSAGEM DE SISTEMA DO HISTÓRICO
        // (transferência / iniciou atendimento / mudou de fila),
        // tanto no formato NOVO (limpo) quanto no formato antigo
        // ("X transferiu de Y para Z · data, hora") gravado anteriormente.
        // Essas NÃO são notas internas escritas por atendente — portanto
        // não devem renderizar o balão âmbar grosso nem o rodapé duplicado
        // "HH:mm • Usuário • dd/MM/yyyy". Renderizam como linha sutil,
        // centralizada, estilo WhatsApp padrão (clean, bonito, discreto).
        const corpo = String(message.body || "").trim();
        const reHistorico =
          /(iniciou\s+o\s+atendimento|transferiu\s+o\s+atendimento|Atendimento\s+(transferido|movido)|moveu\s+o\s+atendimento|transferiu\s+(de|para))/i;
        const ehMensagemDeSistemaDoHistorico =
          (reHistorico.test(corpo) ||
            (/\btransferiu\b/i.test(corpo) &&
              /\d{2}\/\d{2}\/\d{4}/.test(corpo))) &&
          idsMencionados.length === 0;

        if (ehMensagemDeSistemaDoHistorico) {
          // Limpa sufixo antigo " · DD/MM/YYYY, HH:MM" (gravado no backend
          // antigo) — evita repetição quando o frontend já vai mostrar
          // a hora sutilmente. Também tira espaços duplicados.
          const corpoLimpo = corpo
            .replace(/\s*·\s*\d{2}\/\d{2}\/\d{4},\s*\d{1,2}:\d{2}\s*$/i, "")
            .replace(/\s{2,}/g, " ")
            .trim();

          return (
            <React.Fragment key={key}>
              {renderFaixaDoAtendimento(message, index)}
              {renderDailyTimestamps(message, index)}
              <div
                className={cn(
                  "w-full flex justify-center px-4 my-1.5",
                  continuaMesmoAutor ? "mt-0.5 mb-0.5" : "mt-2 mb-2",
                )}
              >
                <div
                  className={cn(
                    "inline-flex max-w-[88%] items-center justify-center",
                    "rounded-full px-4 py-1.5 text-center",
                    "bg-white/75 backdrop-blur-[1px]",
                    "border border-solid border-stone-200/80",
                    "text-[12.5px] leading-snug font-medium",
                    "text-stone-500/95 shadow-[0_1px_0_rgba(0,0,0,0.02)]",
                    "dark:bg-stone-900/50 dark:border-stone-700/60 dark:text-stone-300",
                  )}
                >
                  <span className="truncate">
                    {corpoLimpo || corpo}
                  </span>
                  <span className="ml-2 shrink-0 tabular-nums text-[11px] font-normal text-stone-400/90 dark:text-stone-500">
                    {format(dataMsg, "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={key}>
            {renderFaixaDoAtendimento(message, index)}
            {renderDailyTimestamps(message, index)}

            {/* AVISO DE VISIBILIDADE DA NOTA INTERNA — NÃO É MAIS UM BANNER
                GROSSO ANTES DO BALÃO (era "ridículo" no histórico). Virou um
                chip DISCRETO NO RODAPÉ DA PRÓPRIA NOTA, AO LADO do nome do
                autor, só quando há atendentes mencionados (tudo compacto,
                sem quebrar o fluxo). */}

            <div
              className={cn(
                "w-full flex px-4",
                continuaMesmoAutor ? "mt-1 mb-2" : "my-3",
                ehMinhaNota ? "justify-end" : "justify-start",
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] w-auto rounded-3xl shadow-[0_1px_2px_rgba(0,0,0,0.05)]",
                  "border border-solid border-amber-200/80",
                  "bg-amber-50 text-stone-800",
                  "dark:bg-amber-950/40 dark:text-amber-50 dark:border-amber-700/50",
                )}
              >
                <div
                className={cn(
                  continuaMesmoAutor
                    ? "px-4 py-3 sm:px-5"
                    : "px-5 py-4 sm:px-6",
                )}
              >
                {(
                  message.mediaType === "image" ||
                  message.mediaType === "audio" ||
                  message.mediaType === "video" ||
                  message.mediaType === "document" ||
                  message.mediaType === "sticker" ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard" ||
                  message.mediaType === "contact" ||
                  Boolean(message.mediaUrl)
                ) && (
                  <div
                    className={cn(
                      "mb-2",
                      message.isDeleted && "opacity-50 grayscale",
                    )}
                  >
                    {checkMessageMedia(message)}
                  </div>
                )}
                <div className="wrap-break-word text-[15px] leading-relaxed text-stone-800 dark:text-amber-50/95">
                  {message.body
                    ? <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                    : null}
                </div>

                  <div className="flex w-full items-center justify-end gap-x-1.5 text-[12px] font-normal leading-none text-stone-500/90 dark:text-amber-200/70 flex-wrap justify-items-end">
                    {mencionados.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100/80 px-2 py-0.5 font-medium text-amber-800/90 border border-solid border-amber-200/70 dark:bg-amber-900/50 dark:text-amber-100 dark:border-amber-700/70">
                        <Eye className="h-3 w-3 opacity-80" />
                        <span className="truncate max-w-[8rem]">
                          {mencionados.length === 1
                            ? mencionados[0].name
                            : `${mencionados[0].name} +${mencionados.length - 1}`}
                        </span>
                      </span>
                    )}
                    <span className="tabular-nums">
                      {format(dataMsg, "HH:mm")}
                    </span>
                    <span className="opacity-60">•</span>
                    <span className="truncate">{nomeAutor}</span>
                    <span className="opacity-60">•</span>
                    <span className="tabular-nums">
                      {format(dataMsg, "dd/MM/yyyy")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      }

      if (!message.fromMe) {
        return (
          <React.Fragment key={key}>
            {renderFaixaDoAtendimento(message, index)}
            {renderDailyTimestamps(message, index)}
            {renderMessageDivider(message, index)}
            <div
              id={`mensagem-${message.id}`}
              onDoubleClick={() => responderMensagem(message)}
              title="Dois cliques para responder"
              className={cn(
                bubbleBase,
                "group mr-5 self-start rounded-bl-md bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-100",
                classesDeFigurinha(message),
                destacada === message.id &&
                  "ring-2 ring-amber-400 ring-offset-1",
                classesDeSelecao(message),
              )}
            >
              {renderCapaDeSelecao(message)}
              <button
                type="button"
                id="messageActionsButton"
                disabled={message.isDeleted}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                className="absolute right-7 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
              {renderFavoritoBotao(message)}
              {isGroup && renderAutorDoGrupo(message)}
              {message.quotedMsg && renderQuotedMessage(message)}
              {(
                message.mediaType === "image" ||
                message.mediaType === "audio" ||
                message.mediaType === "video" ||
                message.mediaType === "document" ||
                message.mediaType === "sticker" ||
                message.mediaType === "location" ||
                message.mediaType === "vcard" ||
                message.mediaType === "contact" ||
                Boolean(message.mediaUrl)
              ) && (
                <div className={cn(
                  message.isDeleted && "opacity-50 grayscale",
                  message.quotedMsg && "-mt-0 rounded-t-none overflow-hidden"
                )}>
                  {checkMessageMedia(message)}
                </div>
              )}
            <div
                className={cn(
                  "wrap-break-word pb-1 pr-16 pt-0.5",
                  message.isDeleted && "italic text-muted-foreground",
                )}
              >
                {message.isDeleted && (
                  <span className="mb-1 flex items-center gap-1 text-[11px] not-italic">
                    <Ban className="h-3.5 w-3.5" />
                    Apagada para todos
                    {message.deletedAt
                      ? ` · ${format(parseDataSegura(message.deletedAt), "HH:mm")}`
                      : ""}
                  </span>
                )}
                {message.isForwarded && (
                  <span className="mb-0.5 flex items-center gap-1 text-[11px] italic text-muted-foreground">
                    <Forward className="h-3 w-3" />
                    Encaminhada
                  </span>
                )}
                {/* Selo de "Visualização Única" (ver uma vez do WhatsApp).
                    Ao contrário do cliente do WhatsApp, no CRM a mídia é exibida
                    normalmente (a equipe precisa ver para atender), mas o selo
                    avisa a natureza sensível e que esta mídia foi enviada
                    como "ver uma vez". Exibido SEMPRE quando viewOnce=true
                    (inclusive para o placeholder LID criptografado sem mídia,
                    quando a biblioteca não consegue extrair a mídia e mostra
                    um texto informativo). */}
                {message.viewOnce === true && (
                  <span className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                    <Eye className="h-3 w-3" />
                    Visualização Única
                  </span>
                )}
                {message.templateData ? (
                  <TemplateMessage dados={message.templateData} clicavel={!message.fromMe} />
                ) : (
                  <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                )}
                {renderReacoes(message)}
                <span
                  className={cn(
                    "absolute bottom-0.5 right-1.5 flex items-center gap-1 text-[11px] text-muted-foreground",
                    classesDaHora(message),
                  )}
                >
                  {renderSeloEditada(message)}
                  <span
                    title={format(
                      parseDataSegura(message.createdAt),
                      "dd/MM/yyyy 'às' HH:mm:ss",
                    )}
                  >
                    {format(parseDataSegura(message.createdAt), horaDetalhada ? "HH:mm:ss" : "HH:mm")}
                  </span>
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      const estruturaComNomeAutor =
        mostrarNomeAutorEquipe(message, index) &&
        !temAssinaturaNoCorpo(message) &&
        !isGroup;

      return (
        <React.Fragment key={key}>
          {renderFaixaDoAtendimento(message, index)}
          {renderDailyTimestamps(message, index)}
          {renderMessageDivider(message, index)}
          <div
            id={`mensagem-${message.id}`}
            onDoubleClick={() => responderMensagem(message)}
            title="Dois cliques para responder"
            className={cn(
              bubbleBase,
              "group ml-5 self-end rounded-br-md border-none bg-[#d9fdd3] text-slate-900 dark:bg-[#1f2c17] dark:text-slate-100",
              destacada === message.id &&
                "ring-2 ring-amber-400 ring-offset-1",
              classesDeSelecao(message),
              ehFigurinha(message)
                ? ""
                : estruturaComNomeAutor
                  ? "pb-5 pr-2.5 pt-2"
                  : "pb-1.5 pr-14 pt-2",
              classesDeFigurinha(message),
              ehMensagemOtimista(message) && "opacity-70",
            )}>
            {renderCapaDeSelecao(message)}
            <button
              type="button"
              id="messageActionsButton"
              disabled={message.isDeleted}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              className="absolute right-7 top-0 hidden h-7 w-7 items-center justify-center rounded-full bg-inherit text-muted-foreground opacity-90 group-hover:flex hover:bg-black/5"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            {renderFavoritoBotao(message)}
            {message.quotedMsg && renderQuotedMessage(message)}
            {(
              message.mediaType === "image" ||
              message.mediaType === "audio" ||
              message.mediaType === "video" ||
              message.mediaType === "document" ||
              message.mediaType === "sticker" ||
              message.mediaType === "location" ||
              message.mediaType === "vcard" ||
              message.mediaType === "contact" ||
              Boolean(message.mediaUrl)
            ) && (
              <div className={cn(
                message.isDeleted && "opacity-50 grayscale",
                message.quotedMsg && "-mt-0 rounded-t-none overflow-hidden"
              )}>
                {checkMessageMedia(message)}
              </div>
            )}

            {estruturaComNomeAutor ? (
              <>
                <div
                  className={cn(
                    "wrap-break-word pt-0.5 pb-0.5 pr-2.5",
                    message.isDeleted && "italic text-muted-foreground",
                  )}
                >
                  {message.isDeleted && (
                    <span className="mb-1 flex items-center gap-1 text-[11px] not-italic">
                      <Ban className="h-3.5 w-3.5" />
                      Apagada para todos
                      {message.deletedAt
                        ? ` · ${format(parseDataSegura(message.deletedAt), "HH:mm")}`
                        : ""}
                    </span>
                  )}
                  {message.isForwarded && (
                    <span className="mb-0.5 flex items-center gap-1 text-[11px] italic text-muted-foreground">
                      <Forward className="h-3 w-3" />
                      Encaminhada
                    </span>
                  )}
                  {message.viewOnce &&
                    ["image", "audio", "video", "ptt"].includes(
                      String(message.mediaType || "").toLowerCase()
                    ) && (
                      <span className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                        <Eye className="h-3 w-3" />
                        Visualização Única
                      </span>
                    )}
                  {message.templateData ? (
                    <TemplateMessage dados={message.templateData} />
                  ) : (
                    <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                  )}
                  {renderReacoes(message)}
                </div>
                <div
                  className={cn(
                    "mt-0.5 flex items-center justify-end gap-x-1 px-1.5 pb-1",
                    classesDaHora(message) && "px-1",
                  )}
                >
                  <span
                    className={cn(
                      "flex items-center gap-x-1 text-[11px] text-muted-foreground",
                      classesDaHora(message),
                    )}
                  >
                    {renderSeloEditada(message)}
                    {renderAutorEquipeInline(message, index)}
                    <span
                      title={format(
                        parseDataSegura(message.createdAt),
                        "dd/MM/yyyy 'às' HH:mm:ss",
                      )}
                    >
                      {format(parseDataSegura(message.createdAt), horaDetalhada ? "HH:mm:ss" : "HH:mm")}
                    </span>
                    {foiEnviadoDoAparelho(message) && (
                      <span className="group/icone-aparelho ml-0.5 relative inline-block select-none align-middle">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ height: "8px", width: "8px" }}
                          className="inline-block align-middle text-red-500"
                        >
                          <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
                          <path d="M12 18h.01" />
                        </svg>
                        <span className="pointer-events-none absolute left-1/2 top-full z-[9999] mt-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900/95 px-2 py-1 text-[11px] font-medium text-white shadow-md ring-1 ring-black/5 group-hover/icone-aparelho:block">
                          Enviado do aparelho
                        </span>
                      </span>
                    )}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </>
            ) : (
              <div
                className={cn(
                  "wrap-break-word pb-1 pr-16 pt-0.5",
                  message.isDeleted && "italic text-muted-foreground",
                )}
              >
                {message.isDeleted && (
                  <span className="mb-1 flex items-center gap-1 text-[11px] not-italic">
                    <Ban className="h-3.5 w-3.5" />
                    Apagada para todos
                    {message.deletedAt
                      ? ` · ${format(parseDataSegura(message.deletedAt), "HH:mm")}`
                      : ""}
                  </span>
                )}
                {message.isForwarded && (
                  <span className="mb-0.5 flex items-center gap-1 text-[11px] italic text-muted-foreground">
                    <Forward className="h-3 w-3" />
                    Encaminhada
                  </span>
                )}
                {message.viewOnce &&
                  ["image", "audio", "video", "ptt"].includes(
                    String(message.mediaType || "").toLowerCase()
                  ) && (
                    <span className="mb-0.5 flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                      <Eye className="h-3 w-3" />
                      Visualização Única
                    </span>
                  )}
                {message.templateData ? (
                  <TemplateMessage dados={message.templateData} />
                ) : (
                  <MarkdownWrapper>{corpoVisivel(message)}</MarkdownWrapper>
                )}
                {renderReacoes(message)}
                <span
                  className={cn(
                    "absolute bottom-0.5 right-1.5 flex items-center gap-x-1 text-[11px] text-muted-foreground",
                    classesDaHora(message),
                  )}
                >
                  {renderSeloEditada(message)}
                  <span
                    title={format(
                      parseDataSegura(message.createdAt),
                      "dd/MM/yyyy 'às' HH:mm:ss",
                    )}
                  >
                    {format(parseDataSegura(message.createdAt), horaDetalhada ? "HH:mm:ss" : "HH:mm")}
                  </span>
                  {foiEnviadoDoAparelho(message) && (
                    <span className="group/icone-aparelho ml-0.5 relative inline-block select-none align-middle">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ height: "8px", width: "8px" }}
                        className="inline-block align-middle text-red-500"
                      >
                        <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
                        <path d="M12 18h.01" />
                      </svg>
                      <span className="pointer-events-none absolute left-1/2 top-full z-[9999] mt-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded bg-slate-900/95 px-2 py-1 text-[11px] font-medium text-white shadow-md ring-1 ring-black/5 group-hover/icone-aparelho:block">
                        Enviado do aparelho
                      </span>
                    </span>
                  )}
                  {renderMessageAck(message)}
                </span>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    });

    return (
      <React.Fragment>
        {dataStickyTexto && (
          <div className="sticky top-2 z-40 mx-auto my-2 w-fit">
            <div className="rounded-full border bg-background/85 px-3 py-1 text-center text-[11px] font-medium text-muted-foreground shadow-xs backdrop-blur-md ring-1 ring-black/5">
              {dataStickyTexto}
            </div>
          </div>
        )}
        {viewMessagesList}
      </React.Fragment>
    );
  };


  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setApenasFavoritas(false);
  };

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        anchorRect={anchorRect}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      {/* Barra de filtros. Recolhida por padrão: na maior parte do tempo o que
          se quer é a conversa inteira, e três campos fixos aqui roubariam
          altura da leitura no celular. */}
      <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-background px-3 py-1.5">
        <Button
          variant={filtrosAbertos || filtrando ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setFiltrosAbertos((aberto) => !aberto)}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtrar
        </Button>

        <Button
          variant={modoSelecao ? "secondary" : "ghost"}
          size="sm"
          onClick={() => {
            if (modoEncaminharLote) sairDaSelecaoEncaminhar();
            modoSelecao ? sairDaSelecao() : setModoSelecao(true);
          }}
          title="Escolher arquivos da conversa para baixar"
        >
          <CheckSquare className="h-4 w-4" />
          Selecionar
        </Button>

        <Button
          variant={modoEncaminharLote ? "secondary" : "ghost"}
          size="sm"
          onClick={() => {
            if (modoSelecao) sairDaSelecao();
            if (modoEncaminharLote) sairDaSelecaoEncaminhar();
            else setModoEncaminharLote(true);
          }}
          title="Selecionar várias mensagens para encaminhar de uma vez"
        >
          <Forward className="h-4 w-4" />
          Encaminhar
        </Button>

        <Button
          variant={apenasFavoritas ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setApenasFavoritas((ativo) => !ativo)}
          title="Mostrar só as mensagens que você favoritou"
        >
          <Star
            className={cn(
              "h-4 w-4",
              apenasFavoritas && "fill-amber-400 text-amber-500",
            )}
          />
          Favoritas
        </Button>

        {filtrando && (
          <Button variant="ghost" size="sm" onClick={limparFiltros}>
            <X className="h-4 w-4" />
            Limpar
          </Button>
        )}

        {/* Hora com segundos. Duas mensagens no mesmo minuto — uma rajada do
            cliente, uma resposta que cruzou com ela — mostravam a mesma hora,
            e não dava para saber qual veio antes. */}
        <Button
          variant="ghost"
          size="sm"
          aria-pressed={horaDetalhada}
          title={
            horaDetalhada
              ? "Mostrando hora com segundos"
              : "Mostrar a hora com segundos"
          }
          onClick={() => setHoraDetalhada((v) => !v)}
          className={cn(horaDetalhada && "text-primary")}
        >
          <Clock className="h-4 w-4" />
          Segundos
        </Button>

        {/* A busca fica na mesma barra, mas não recorta a conversa: ela só
            aponta para onde ir, e o contador diz quantas vezes o termo
            aparece — como no WhatsApp. */}
        <div className="ml-auto flex items-center gap-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              placeholder="Buscar na conversa"
              aria-label="Buscar na conversa"
              className="h-8 w-40 pl-7 text-xs sm:w-52"
            />
          </div>

          {buscando && (
            <span className="text-xs text-muted-foreground">…</span>
          )}

          {!buscando && termoBusca.trim().length >= 2 && (
            <>
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {achados.length
                  ? `${indiceAchado + 1} de ${achados.length}`
                  : "nada"}
                {/* De qual chamado é o resultado: a busca cobre a conversa
                    inteira do contato, e sem isto não dá para saber se o que
                    foi encontrado é de hoje ou do atendimento do mês passado. */}
                {achados[indiceAchado] &&
                  Number(achados[indiceAchado].ticketId) !==
                    Number(ticketId) && (
                    <span className="ml-1 text-primary">
                      · #{achados[indiceAchado].ticketId}
                    </span>
                  )}
              </span>

              {achados.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Resultado anterior (mais recente)"
                    onClick={() =>
                      irParaAchado(
                        (indiceAchado - 1 + achados.length) % achados.length,
                      )
                    }
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Próximo resultado (mais antigo)"
                    onClick={() =>
                      irParaAchado((indiceAchado + 1) % achados.length)
                    }
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                title="Limpar a busca"
                onClick={limparBusca}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>

        {filtrosAbertos && (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              De
              <Input
                type="date"
                value={dataInicio}
                max={dataFim || undefined}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Até
              <Input
                type="date"
                value={dataFim}
                min={dataInicio || undefined}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-8 w-[9.5rem]"
              />
            </label>
          </div>
        )}
      </div>

      {/* Barra do modo seleção. Fica presa acima da conversa: rolando atrás
          dela, o que foi marcado e o botão de baixar continuam à mão. */}
      {modoSelecao && (
        <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-primary/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selecionadas.size} arquivo{selecionadas.size === 1 ? "" : "s"}{" "}
            selecionado{selecionadas.size === 1 ? "" : "s"}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={alternarTodosDaConversa}
            disabled={!arquivosDaConversa.length}
          >
            {todosArquivosMarcados ? "Desmarcar todos" : "Marcar todos à vista"}
          </Button>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={() =>
                baixarArquivos(Array.from(selecionadas), "selecionados")
              }
              disabled={!selecionadas.size || !!baixando}
            >
              {baixando === "selecionados" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar selecionados ({selecionadas.size})
            </Button>

            {/* Lista vazia = tudo o que o atendimento tem no banco, e não só o
                que já foi rolado na tela. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => baixarArquivos([], "tudo")}
              disabled={!!baixando}
            >
              {baixando === "tudo" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Baixar tudo da conversa
            </Button>

            <Button variant="ghost" size="sm" onClick={sairDaSelecao}>
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          </div>

          <p className="w-full text-xs text-muted-foreground">
            Toque nas mensagens com anexo para marcar. Imagens, vídeos, áudios,
            PDFs e documentos entram num ZIP separado por tipo.
          </p>
        </div>
      )}

      {/* Barra do modo encaminhar em lote. Mesmo posicionamento fixo sobre a
          conversa para não perder o contexto do que já foi marcado ao rolar. */}
      {modoEncaminharLote && (
        <div className="flex flex-none flex-wrap items-center gap-2 border-b bg-emerald-500/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selecionadasEncaminhar.size}{" "}
            mensagem{selecionadasEncaminhar.size === 1 ? "" : "s"} selecionada
            {selecionadasEncaminhar.size === 1 ? "" : "s"}
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={alternarTodasEncaminhar}
            disabled={!todasMensagensParaEncaminhar.length}
          >
            {todasMarcadasParaEncaminhar
              ? "Desmarcar todas"
              : "Marcar todas à vista"}
          </Button>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={abrirEncaminhamentoLote}
              disabled={!selecionadasEncaminhar.size}
              className="gap-2"
            >
              <Forward className="h-4 w-4" />
              Encaminhar selecionadas ({selecionadasEncaminhar.size})
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={sairDaSelecaoEncaminhar}
            >
              <X className="h-4 w-4" />
              Cancelar
            </Button>
          </div>

          <p className="w-full text-xs text-muted-foreground">
            Toque nas mensagens para marcar. A ordem da conversa é preservada
            no encaminhamento (texto, imagem, áudio, vídeo, PDF, figurinha,
            localização e cartão).
          </p>
        </div>
      )}

      <ForwardMessageModal
        open={modalEncaminharAberto}
        onClose={() => {
          setModalEncaminharAberto(false);
          // Fechar o modal = usuário completou ou cancelou o encaminhamento; em
          // ambos os casos é melhor voltar à conversa normal e não deixar o
          // estado de seleção multiplo ligado atrapalhando cliques nas bolhas.
          sairDaSelecaoEncaminhar();
        }}
        messages={encaminharLoteSelecionadas}
      />

      <VisualizadorDeArquivo
        open={visualizando !== null}
        onClose={() => setVisualizando(null)}
        arquivos={arquivosDaConversa}
        indice={visualizando ?? 0}
        onIndice={setVisualizando}
      />

      {/* ── Modal Histórico de Edições ────────────────────────────────── */}
      <Dialog
        open={editHistoryOpen && mensagemEmEdicao !== null}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setEditHistoryOpen(false);
            setMensagemEmEdicao(null);
          } else {
            setEditHistoryOpen(true);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-muted-foreground" />
              Histórico de edições
            </DialogTitle>
            <DialogDescription>
              {mensagemEmEdicao?.editedAt
                ? `Última edição em ${format(
                    parseISO(mensagemEmEdicao.editedAt),
                    "dd/MM/yyyy HH:mm",
                    { locale: ptBR },
                  )}`
                : ""}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            {(() => {
              if (!mensagemEmEdicao) return null;
              const temHistory =
                Array.isArray(mensagemEmEdicao.editHistory) &&
                mensagemEmEdicao.editHistory.length > 0;
              const versoes = [];

              if (temHistory) {
                const ordenado = [...mensagemEmEdicao.editHistory].sort(
                  (a, b) =>
                    new Date(a.editedAt || 0) - new Date(b.editedAt || 0),
                );
                ordenado.forEach((h) =>
                  versoes.push({
                    body: h.body,
                    editedAt: h.editedAt,
                    atual: false,
                  }),
                );
              } else if (
                mensagemEmEdicao.previousBody !== null &&
                mensagemEmEdicao.previousBody !== undefined &&
                String(mensagemEmEdicao.previousBody).length > 0
              ) {
                // Fallback para mensagens pré-migration: a coluna previousBody
                // guarda a penúltima versão. Mostra como "versão anterior".
                versoes.push({
                  body: mensagemEmEdicao.previousBody,
                  editedAt:
                    mensagemEmEdicao.editedAt || mensagemEmEdicao.createdAt,
                  atual: false,
                });
              }

              // Versão ATUAL sempre no fim da lista.
              versoes.push({
                body: mensagemEmEdicao.body,
                editedAt:
                  mensagemEmEdicao.editedAt || mensagemEmEdicao.createdAt,
                atual: true,
              });

              return versoes.map((v, idx) => {
                const horario = v.editedAt
                  ? format(
                      parseISO(v.editedAt),
                      "dd/MM/yyyy HH:mm",
                      { locale: ptBR },
                    )
                  : "";
                return (
                  <div
                    key={`${idx}-${v.atual ? "atual" : "ant"}`}
                    className={cn(
                      "relative rounded-lg border p-3 pr-10 text-sm",
                      v.atual
                        ? "border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/20"
                        : "bg-muted/50",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 w-1 rounded-l-lg",
                        v.atual ? "bg-emerald-500" : "bg-border",
                      )}
                    />
                    {v.atual && (
                      <span className="absolute right-2 top-2 inline-flex items-center rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-medium text-emerald-50 shadow-xs">
                        Versão atual
                      </span>
                    )}
                    <div className="mb-1 text-[11px] text-muted-foreground">
                      {horario || "Horário desconhecido"}
                    </div>
                    <div className="whitespace-pre-wrap break-words leading-relaxed">
                      {v.body || (
                        <span className="italic text-muted-foreground">
                          (mensagem vazia)
                        </span>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                setEditHistoryOpen(false);
                setMensagemEmEdicao(null);
              }}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A textura é clara; no tema escuro ela entra esmaecida sobre o fundo
          do tema para as bolhas não perderem contraste. */}
      <div
        id="messagesList"
        onScroll={handleScroll}
        className="scrollbar-discreta relative flex flex-1 flex-col overflow-y-auto bg-muted/40"
      >
        {/* Este wrapper existe para a textura ter em que se ancorar.
            Ela era `absolute inset-0` direto no contêiner de rolagem, e
            elemento absoluto ali é dimensionado pela área VISÍVEL, não pela
            altura do conteúdo: passando de uma tela de mensagens, o resto
            rolava para fora da textura e o fundo ficava branco.
            Aqui a altura acompanha as mensagens, e o min-h-full garante a
            textura inteira também quando há pouca conversa. O shrink-0 é
            obrigatório: o pai é flex-col, e item flex encolhe para caber em
            vez de crescer — sem ele o wrapper voltava a ter a altura da
            janela e o problema continuava. */}
        <div className="relative flex min-h-full shrink-0 flex-col p-3 pb-24 sm:p-5 sm:pb-5">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-repeat opacity-100 dark:opacity-[0.06]"
            style={{ backgroundImage: `url(${whatsBackground})` }}
          />
          <div className="relative flex flex-1 flex-col">
            {/* Antes o `renderMessages` só era chamado com a lista cheia, e
                por isso o aviso de "nada encontrado" nunca aparecia. */}
            {messagesList.length > 0 || filtrando ? renderMessages() : []}
          </div>
        </div>
      </div>
      {loading && (
        <div className="absolute left-1/2 top-3 -translate-x-1/2">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500 opacity-70" />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
